import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Banknote, CreditCard, FileText, Printer, Wallet } from "lucide-react";
import { KittensIllustration, PawIllustration } from "./wizardIllustrations";
import {
  useCustom,
  useCustomMutation,
  useGetLocale,
  usePermissions,
  useTranslate,
} from "@refinedev/core";
import { Button, Field, FieldShell, Modal, PinIcon, SelectField, TextareaField } from "@fawkes/ui";
import { StepIntro, WizardModal } from "./WizardModal";
import { PaymentStep } from "../components/PaymentStep";
import { betalingOordeel } from "../components/betalingRegels";
import { afgerondeEuro, kortingRegelTeHoog, kortingTotaal, kortingVanRegel, regelNaKorting }
  from "../components/KortingVeld";
import { ComboBox } from "./ComboBox";
import { AnimalThumb, animalRowText } from "./AnimalSubwizard";
import { useAnimalPhotoUrls } from "../api/useAnimalPhotoUrls";
import { ProductTariefPicker, type GekozenProductRegel } from "../components/ProductTariefPicker";
import { KostenregelBtw, kostenregelZonderBtw, useBtwPercentages } from "../components/BtwKeuze";
import { useModuleAvailability } from "../api/modules";
import { FoundLocationMap } from "../components/FoundLocationMap";
import { resolveGemeenteId, useFoundLocationGeocoding } from "../components/useFoundLocationGeocoding";
import { useVestigingMapCenter } from "../components/useVestigingMapCenter";
import { SignaturePad } from "../components/SignaturePad";
import { RelationContactPanel, addressLine, type KlantDetail } from "../components/RelationContactPanel";
import { OwnerConfirmCard, type KnownOwnerView } from "../components/OwnerConfirmCard";
import { RelationPicker } from "../components/RelationPicker";
import { formatDate, formatEuro, todayIso } from "../i18n/format";
import { printVerklaring } from "../components/printVerklaring";
import { downloadWizardDocumentPdf, fetchWizardDocumentPreview } from "../api/wizardDocument";

// ── types ────────────────────────────────────────────────────────────────────────────────────────

interface OptionRow {
  id: number;
  label: string;
  color: string | null;
}
interface ExchangeShelter {
  id: number;
  naam: string;
  email: string | null;
  telefoon: string | null;
  woonplaats: string | null;
}
interface VertrekSoort {
  id: number;
  label: string;
  option: string; // ADOPTION | RETURN_TO_OWNER | EUTHANASIA | DECEASED | NATURE_RELEASE | EXCHANGE | DEFAULT
  // Greyed in the picker (Exchange — Epic 3 — and Overig): shown "tijdelijk niet beschikbaar".
  alwaysDisabled: boolean;
}
interface AnimalSummary {
  id: number;
  naam: string;
  diersoort: string | null;
  ras: string | null;
  chipPasnummer: string | null;
  overleden: boolean;
}
interface SelectedAnimal {
  id: number;
  label: string;
  chip: string | null;
  // Jmix summary-row facts (chip | naam | soort | ras | geslacht | kleur | vacht + 56px thumbnail).
  naam: string;
  soort: string | null;
  ras: string | null;
  geslacht: string | null;
  kleur: string | null;
  vacht: string | null;
  fotoUrl: string | null;
}
// One card in the GET /api/animals/present feed — the vestiging-scoped "Aanwezige dieren" grid the
// Jmix release picker shows (AnimalSearchResultsFragment releasePresentOverviewMode). Subset of the
// api AnimalCardResponse the wizard actually renders.
interface PresentAnimalCard {
  id: number;
  naam: string | null;
  fotoUrl: string | null;
  diersoort: string | null;
  ras: string | null;
  kleur: string | null;
  vacht: string | null;
  geslacht: string | null;
  chipPasnummer: string | null;
}
// The active (open) stay of a released animal — backs the return-to-owner owner pre-suggestion and the
// opvangkosten day-count / stay card. Mirrors the api IntakeStayResponse (GET …/intake-stay).
interface IntakeStay {
  animalId: number | null;
  intakeDate: string | null;
  ownerId: number | null;
  ownerLabel: string | null;
  ambulanceName: string | null;
  ambulanceRitNummer: string | null;
  /** Where the animal was found/caught on intake — a nature release can put it back exactly there. */
  foundLocation: IntakeFoundLocation | null;
}
interface IntakeFoundLocation {
  straat: string | null;
  huisnummer: string | null;
  huisnummerToevoeging: string | null;
  postcode: string | null;
  woonplaats: string | null;
  gemeenteId: number | null;
  gemeenteLabel: string | null;
  opmerking: string | null;
  latitude: number | null;
  longitude: number | null;
}
// One manual cost line (return / adoption). Default return lines (opvangkosten + ambulance/vet/admin)
// carry fixed: true — their product label is locked and they cannot be removed (mirrors the Jmix
// ReturnToOwnerCostLinesStateService default lines), but their amount stays editable.
interface CostLine {
  id: number;
  description: string;
  amount: string;
  fixed?: boolean;
  // Het BTW-percentage: uit de catalogus of de tariefmotor, anders zelf te kiezen (verplicht bij een bedrag).
  btw?: number | null;
  /** Korting op déze regel; de btw ervan is die van de regel zelf. */
  korting?: string;
}
type Notice = null | "conflict" | "invalid" | "error";

// Tariff auto-proposal (POST /api/tarief-voorstel) — the React parity for the Jmix wizards'
// "Tarief voorstellen" affordance. The engine returns an explicit status so the UI never guesses
// from a null amount; on a CONFLICT it carries the equally-specific rule names for the message.
interface TariefVoorstelResponse {
  status: "MATCHED" | "NO_MATCH" | "CONFLICT" | "MANUAL_QUOTE";
  regelNaam: string | null;
  omschrijving: string | null;
  bedrag: number | null;
  btwPercentage?: number | null;
  conflictRegels: string[];
  toelichting: string;
}

// ── PetBase chip-registration capture (Epic 4) ──────────────────────────────────────────────────────
// One per-animal draft for the release chip step; PATCHed into the WizardData JSON under `chipCaptures`
// and enqueued server-side on /complete (the chipcheck → afvoer → aanvoer state machine runs async — no
// immediate verdict here; the per-animal status surface reflects it). Mirrors the surrender capture step.
interface ReleaseChipCapture {
  chipNumber: string;
  // "Chip is geregistreerd" — captured-only (the dispatcher does its own live chipcheck to gate the
  // afvoer; this flag does not gate the enqueue), faithful to the Jmix submitAllForRelease behaviour.
  chipRegistered: boolean;
  passportNumber: string;
  kenmerken: string;
}
// The Dier facts shown on the chip step (GET /api/animals/{id} — AnimalDetailResponse subset). Name/sex/
// species/breed/coat/colour/birthDate are read-only; paspoort/kenmerken seed the editable capture fields.
interface ChipAnimalDetail {
  naam: string | null;
  geslacht: string | null;
  gecastreerd: boolean;
  diersoort: string | null;
  ras: string | null;
  kruising: boolean;
  vacht: string | null;
  kleur: string | null;
  paspoortNummer: string | null;
  kenmerken: string | null;
  geboortedatum: string | null;
}

function isExactly15Digits(value: string): boolean {
  return /^\d{15}$/.test(value.replace(/\s/g, ""));
}

/**
 * Verbatim-Dutch validation of one release chip capture (FR16); returns the i18n keys of any errors.
 * An EMPTY chip number is allowed (Jmix SurrenderChipRegistrationFragment: exactly 15 digits or
 * blank) — the operator can blank out a wrong chip; the server skips blanks as SKIPPED_NO_CHIP.
 */
function releaseChipErrors(d: ReleaseChipCapture | undefined): string[] {
  const chip = (d?.chipNumber ?? "").replace(/\s/g, "");
  return chip === "" || isExactly15Digits(chip) ? [] : ["uitboeken.petbaseChip.validation.chipFormat"];
}

/**
 * One chipped animal's release chip card: the read-only Dier facts (GET /api/animals/{id}) plus the
 * editable chip number / paspoort / kenmerken and the "Chip is geregistreerd" checkbox (FR16). Own
 * component so the per-animal fetch is one hook per card (no hooks-in-a-loop). The paspoort/kenmerken
 * seed once from the fetched detail via {@code onSeedDetail} (parent-deduped so a remount can't clobber
 * operator edits); the chip number is seeded by the parent from the animal feed.
 */
function ReleaseChipCard({
  animalId,
  label,
  draft,
  onPatch,
  onSeedDetail,
  deregister,
  t,
}: {
  animalId: number;
  label: string;
  draft: ReleaseChipCapture | undefined;
  onPatch: (id: number, patch: Partial<ReleaseChipCapture>) => void;
  onSeedDetail: (id: number, patch: Partial<ReleaseChipCapture>) => void;
  // Death flows (euthanasia/deceased) render a deregistration card: read-only name + chip and a single
  // "afgemeld" checkbox, matching the Jmix EuthanasiaChipDeregistrationFragment (no editable paspoort/
  // kenmerken — the chip is never corrected on the way out for a death). Transfers keep the full capture.
  deregister: boolean;
  t: (k: string) => string;
}) {
  const { data } = useCustom({ url: `animals/${animalId}`, method: "get" });
  const d = (data?.data as ChipAnimalDetail | undefined) ?? null;
  const seeded = useRef(false);
  useEffect(() => {
    // No paspoort/kenmerken to seed in deregister mode — those fields aren't shown or sent.
    if (!d || seeded.current || deregister) return;
    seeded.current = true;
    onSeedDetail(animalId, { passportNumber: d.paspoortNummer ?? "", kenmerken: d.kenmerken ?? "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d, animalId, deregister]);

  // Deregistration card (euthanasia/deceased): name + chip read-only, one "Chip is afgemeld" checkbox per
  // animal. The checkbox is captured-only (faithful to Jmix: it drives no submission — the dispatcher's live
  // chipcheck gates the afvoer@10); kept so the operator confirms each animal, as the Jmix fragment does.
  if (deregister) {
    return (
      <div className="fawkes-chipreg__card">
        <span className="fawkes-chipreg__animal">{label}</span>
        <div className="fawkes-chipreg__readonly">
          <SummaryRow label={t("inboeken.chip.field.name")} value={d?.naam ?? "—"} />
          <SummaryRow label={t("inboeken.chip.field.chipNumber")} value={draft?.chipNumber || "—"} />
        </div>
        <label className="fawkes-inboeken__terms-agree">
          <input
            type="checkbox"
            checked={draft?.chipRegistered ?? false}
            onChange={(e) => onPatch(animalId, { chipRegistered: e.target.checked })}
          />
          <span>{t("uitboeken.chip.deregister")}</span>
        </label>
      </div>
    );
  }

  const yesNo = (v: boolean | undefined) => t(v ? "common.yes" : "common.no");
  const errors = releaseChipErrors(draft);
  return (
    <div className="fawkes-chipreg__card">
      <span className="fawkes-chipreg__animal">{label}</span>
      <div className="fawkes-chipreg__readonly">
        <SummaryRow label={t("inboeken.chip.field.name")} value={d?.naam ?? "—"} />
        <SummaryRow label={t("inboeken.chip.field.gender")} value={d?.geslacht ?? "—"} />
        <SummaryRow label={t("inboeken.chip.field.species")} value={d?.diersoort ?? "—"} />
        <SummaryRow label={t("inboeken.chip.field.breed")} value={d?.ras ?? "—"} />
        <SummaryRow label={t("inboeken.chip.field.coat")} value={d?.vacht ?? "—"} />
        <SummaryRow label={t("inboeken.chip.field.color")} value={d?.kleur ?? "—"} />
        <SummaryRow label={t("inboeken.chip.field.birthDate")} value={d?.geboortedatum ?? "—"} />
        <SummaryRow label={t("inboeken.chip.field.castrated")} value={yesNo(d?.gecastreerd)} />
        <SummaryRow label={t("inboeken.chip.field.crossBreed")} value={yesNo(d?.kruising)} />
      </div>
      <div className="fawkes-chipreg__grid">
        <Field
          label={t("inboeken.chip.field.chipNumber")}
          value={draft?.chipNumber ?? ""}
          placeholder={t("inboeken.chip.field.chipNumber.placeholder")}
          onChange={(e) => onPatch(animalId, { chipNumber: e.target.value })}
        />
        <Field
          label={t("inboeken.chip.field.passport")}
          value={draft?.passportNumber ?? ""}
          onChange={(e) => onPatch(animalId, { passportNumber: e.target.value })}
        />
        <Field
          label={t("inboeken.chip.field.features")}
          value={draft?.kenmerken ?? ""}
          onChange={(e) => onPatch(animalId, { kenmerken: e.target.value })}
        />
      </div>
      <label className="fawkes-inboeken__terms-agree">
        <input
          type="checkbox"
          checked={draft?.chipRegistered ?? false}
          onChange={(e) => onPatch(animalId, { chipRegistered: e.target.checked })}
        />
        <span>{t("uitboeken.petbaseChip.registered")}</span>
      </label>
      {errors.length > 0 && (
        <ul className="fawkes-chipreg__errors">
          {errors.map((k) => (
            <li key={k} className="fawkes-inboeken__field-error">{t(k)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

const ADOPTION = "ADOPTION";
const RETURN_TO_OWNER = "RETURN_TO_OWNER";
const EUTHANASIA = "EUTHANASIA";
const DECEASED = "DECEASED";
const NATURE_RELEASE = "NATURE_RELEASE";
const EXCHANGE = "EXCHANGE";

// Return-to-owner housing rate: opvangkosten = days × €10/day, mirroring the Jmix
// ReturnToOwnerCostLinesStateService.DEFAULT_DAILY_RATE. A separate tariff-engine auto-proposal task
// will replace this hardcoded rate; the seeded line stays operator-editable in the meantime.
const RETURN_DAILY_RATE = 10;
// Source recorded for the chosen original owner (audit, mirrors Jmix setReturnOriginalOwnerSource).
const ORIGINAL_OWNER_SOURCE_INTAKE = "INTAKE_RELATION";
const ORIGINAL_OWNER_SOURCE_MANUAL = "MANUAL_SEARCH";

// Adoption default cost line — the base adoption fee the costs step opens with, replaced silently by
// the tariff engine when it matches (Jmix AdoptionCostLinesStateService.DEFAULT_PRODUCT/DEFAULT_AMOUNT
// + AdoptionCostsFragment.vulTariefAutomatischIn). Default-ness is by product name, like Jmix.
const ADOPTION_DEFAULT_PRODUCT = "Adoptiebijdrage";
const ADOPTION_DEFAULT_AMOUNT = 100;

function isDefaultAdoptionLine(line: { description: string }): boolean {
  return line.description.trim().toLowerCase() === ADOPTION_DEFAULT_PRODUCT.toLowerCase();
}

// Payment method rows (Jmix AdoptionPaymentFragment: Pin / Contant / Later betalen), reused by the
// adoption AND return-to-owner payment steps (Jmix reuses the same fragment for retour). Module scope —
// stepDefs render functions execute during render, so component-body consts would hit the TDZ.
type ReleasePayMethod = "pin" | "cash" | "later";
/** De tegel is een korte weg naar de bedragregels; "Combinatie" laat je zelf verdelen. */
type ReleasePayTile = "pin" | "cash" | "combi" | "invoice";
const RELEASE_PAY_METHODS: { key: ReleasePayTile; labelKey: string; icon: ReactNode }[] = [
  { key: "pin", labelKey: "inboeken.payment.pin", icon: <CreditCard size={18} aria-hidden="true" /> },
  { key: "cash", labelKey: "inboeken.payment.cash", icon: <Banknote size={18} aria-hidden="true" /> },
  { key: "combi", labelKey: "inboeken.payment.combi", icon: <Wallet size={18} aria-hidden="true" /> },
  { key: "invoice", labelKey: "inboeken.payment.invoice", icon: <FileText size={18} aria-hidden="true" /> },
];
const RELEASE_PAY_ROWS: { key: ReleasePayMethod; labelKey: string; icon: ReactNode }[] = [
  { key: "pin", labelKey: "inboeken.payment.pin", icon: <CreditCard size={16} aria-hidden="true" /> },
  { key: "cash", labelKey: "inboeken.payment.cash", icon: <Banknote size={16} aria-hidden="true" /> },
  { key: "later", labelKey: "inboeken.payment.invoiceOpen", icon: <FileText size={16} aria-hidden="true" /> },
];

/** Een totaal als los invulbaar bedrag ("50.00"), om de bedragregels voor te vullen. */
function formatPayAmount(value: number): string {
  return value > 0 ? value.toFixed(2) : "";
}

// Fixed vocabulary lists — business-specified ComboBox values, mirroring the Jmix
// EuthanasiaDetailsFragment / DeceasedDetailsFragment lists verbatim (stored literally, not a ref
// table — same pattern as the intake SURRENDER_REASONS list).
const EUTHANASIA_PERFORMER_INTERNAL = "Interne dierenarts";
const EUTHANASIA_PERFORMER_EXTERNAL = "Dierenarts extern";
const EUTHANASIA_PERFORMERS = [EUTHANASIA_PERFORMER_INTERNAL, EUTHANASIA_PERFORMER_EXTERNAL];
const EUTHANASIA_REASONS = ["Eendagsnestje", "Gedragsprobleem", "Medische indicatie", "Overig"];
const DECEASED_CAUSE_OTHER = "Anders";
const DECEASED_CAUSES = ["Ziekte", "Ouderdom", "Ongeval", "Eendagsnestje", DECEASED_CAUSE_OTHER];

// ── type-card presentation ─────────────────────────────────────────────────────────────────────────
// Per-release-type icon + description, mirroring the Jmix ShelterReleaseTypeCardFragment (icon by
// option, .info descriptions). Icons are the line-art SVGs ported from the Jmix release assets.

function typeKey(s: VertrekSoort): string {
  switch (s.option) {
    case ADOPTION:
      return "adoption";
    case RETURN_TO_OWNER:
      return "return";
    case EUTHANASIA:
      return "euthanasia";
    case DECEASED:
      return "deceased";
    case NATURE_RELEASE:
      return "nature";
    case EXCHANGE:
      return "exchange";
    default:
      return "other";
  }
}

const TYPE_ICON: Record<string, string> = {
  adoption: "/release/icon_release_geplaatst.svg",
  return: "/release/icon_release_retour.svg",
  euthanasia: "/release/icon_release_euthanasie.svg",
  deceased: "/release/icon_release_overleden.svg",
  nature: "/release/icon_release_uitgezet.svg",
  exchange: "/release/icon_release_uitwisseling.svg",
  other: "/release/icon_release_overig.svg",
};

/**
 * The order the type cards are shown in (maintainer): plaatsing first, then the two hand-overs, then
 * the two deaths, then uitzetten — and whatever is unavailable ("Overig") last. The feed's own order is
 * alphabetical-ish and led with the greyed card, which is not how anyone reads this step.
 */
const TYPE_ORDER = ["adoption", "return", "exchange", "deceased", "euthanasia", "nature", "other"];

const TYPE_DESC_KEY: Record<string, string> = {
  adoption: "uitboeken.type.adoption.info",
  return: "uitboeken.type.return.info",
  euthanasia: "uitboeken.type.euthanasia.info",
  deceased: "uitboeken.type.deceased.info",
  nature: "uitboeken.type.nature.info",
  exchange: "uitboeken.type.exchange.info",
};

// ── small data hooks ───────────────────────────────────────────────────────────────────────────

function useFeed<T>(url: string, enabled = true): T[] {
  const { data } = useCustom({ url, method: "get", queryOptions: { enabled } });
  return (data?.data as T[] | undefined) ?? [];
}

function noticeFor(err: unknown): Notice {
  const code = (err as { statusCode?: number }).statusCode;
  return code === 409 ? "conflict" : code === 422 ? "invalid" : "error";
}

// Jmix ShelterReleaseSelectAnimalCountFragment bound: Één = 1, Meerdere = 2–10.
const MAX_ANIMAL_COUNT = 10;

/** Parse a euro amount from a free-text field (accepts a comma or dot decimal); null when blank/NaN. */
function parseAmount(value: string): number | null {
  const s = value.trim().replace(/\s/g, "").replace(",", ".");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

// ── the wizard ───────────────────────────────────────────────────────────────────────────────────

/**
 * Uitboeken (release) wizard — a full-page screen that takes one or more animals OUT of the shelter,
 * closing each animal's open {@code asiel_verblijf} via the generic WizardData store / {@code POST
 * /api/release}. Steps: Soort → Dieren → type-specific → Controle. The OUT counterpart of the Inboeken
 * wizard, reusing its infra (dynamic key-based steps, type icon-cards, SignaturePad, RelationContactPanel,
 * print, formatEuro) and CSS. The animal-eligibility is INVERTED vs intake: release ENABLES resident
 * animals and disables non-resident ones (reuses the resident-animal-ids feed).
 *
 * <p>Per-type steps (mirroring the Jmix ShelterRelease fragments): ADOPTION = new-owner + cost lines +
 * placement terms/signature + payment (a concept-factuur is generated server-side from the cost lines,
 * keyed on the new owner); RETURN_TO_OWNER = owner + return details + retour declaration/signature;
 * EUTHANASIA = details (date/performer/vet/reason/remarks) + chip-deregistration; DECEASED = details
 * (date/cause/vet/remarks); NATURE_RELEASE = location + Leaflet map. EXCHANGE stays greyed (Epic 3).
 * Deferred (mirrors intake): the tariff-engine auto-proposal, server-side PDF documents, and the live
 * PetBase chip de-registration (the step captures intent into the WizardData JSON only).
 */

// wizard-redesign: group the release steps into four stepper phases (claude.ai/design "Fawkes —
// In/uitcheck wizard"). Monotonic along the real step order (count → animals → type → destination
// (relation/party/details) → afhandeling (costs/terms/payment/chip) → summary).
const UITBOEKEN_PHASES = ["Gegevens", "Bestemming", "Afhandeling", "Bevestigen"];
const UITBOEKEN_PHASE_OF: Record<string, number> = {
  count: 0,
  animals: 0,
  type: 0,
  adoptionRelation: 1,
  returnRelation: 1,
  returnDetails: 1,
  exchangeParty: 1,
  exchangeDetails: 1,
  euthanasiaDetails: 1,
  deceasedDetails: 1,
  natureDetails: 1,
  adoptionCosts: 2,
  adoptionTerms: 2,
  adoptionPayment: 2,
  returnDeclaration: 2,
  returnPayment: 2,
  petbaseChip: 2,
  summary: 3,
};
const uitboekenPhaseOf = (key: string) => UITBOEKEN_PHASE_OF[key] ?? 0;

export function ReleaseWizard({
  preselectedAnimalId,
  onDone,
  onCancel,
  onExpand,
}: {
  preselectedAnimalId?: string | null;
  /** De uitgeboekte dieren, zodat de host meteen naar het dier kan springen dat je net afhandelde. */
  onDone: (animalIds?: number[]) => void;
  onCancel: () => void;
  // wizard-dialog-mode: present when hosted as a dialog — switches to the full-page view (the host
  // keeps this component mounted, so in-progress entries and the open draft survive the switch).
  onExpand?: () => void;
}) {
  const t = useTranslate();
  const getLocale = useGetLocale();
  const locale = getLocale() ?? "nl";
  // The "Nieuwe relatie" affordance is shown only when the operator also holds the relatie module's own
  // create authority (ROLE_RELATIE_EDIT) — we reuse POST /api/relaties rather than backdooring a create
  // path under the release authority, so relation governance stays in the relatie module.
  const { data: permissions } = usePermissions<string[]>();
  const canCreateRelatie = Array.isArray(permissions) && permissions.includes("ROLE_RELATIE_EDIT");
  // wizard-jmix-parity: `step` = the expanded card, `furthest` = the highest card reached — every
  // other reached step renders as a collapsed summary bar (Jmix StepCard). A locked launch (the
  // Verblijf UIT tile) starts past the count+animals steps, which show as pre-completed bars.
  const startLocked = preselectedAnimalId != null && preselectedAnimalId !== "";
  const [step, setStep] = useState(startLocked ? 2 : 0);
  // De vraag die openstaat terwijl iemand hem beantwoordt: wat er daarna moet gebeuren.
  const [restantVraag, setRestantVraag] = useState<null | (() => void)>(null);
  const [restantBedrag, setRestantBedrag] = useState(0);
  const [furthest, setFurthest] = useState(startLocked ? 2 : 0);
  // Jmix ShelterReleaseSelectAnimalCountFragment: Één dier / Meerdere dieren (2–10). null = unchosen.
  const [multi, setMulti] = useState<boolean | null>(startLocked ? false : null);
  const [countInput, setCountInput] = useState("2");
  const [notice, setNotice] = useState<Notice>(null);

  // Step — release type (now AFTER the animals, matching the Jmix step order)
  const soorten = useFeed<VertrekSoort>("release/reference/vertreksoorten");
  const [soortId, setSoortId] = useState<string>("");
  const option = useMemo(
    () => soorten.find((s) => String(s.id) === soortId)?.option ?? "DEFAULT",
    [soorten, soortId],
  );

  // Step 2 — animals (resident only). The list backs both the preselect-label lookup and the search.
  // Launched from a Dier's UIT tile: fetch THAT animal, not the animal list. The list was pulled with
  // `pagination: off` purely to look up one row — on a shelter with a real `dier` table that returns a
  // capped page, so the preselected animal was usually NOT in it: the wizard opened with no animal at
  // all, which silently disabled everything keyed on it (the TNRC found-location card, the tariff).
  const preselectedQuery = useCustom<AnimalSummary>({
    url: `animals/${preselectedAnimalId}`,
    method: "get",
    queryOptions: { enabled: preselectedAnimalId != null && preselectedAnimalId !== "" },
  });
  const preselectedAnimal = preselectedQuery.data?.data ?? null;
  const [selected, setSelected] = useState<SelectedAnimal[]>([]);
  // Zie kiesDier(): "Selecteer" schuift de wizard zelf door, maar pas één render later.
  const [doorschuiven, setDoorschuiven] = useState(false);
  // Locked to a single animal when launched from a Dier's Verblijf UIT tile.
  const locked = preselectedAnimalId != null && preselectedAnimalId !== "";

  // The Jmix release picker is the vestiging-scoped "Aanwezige dieren" photo-card grid (parity-audit
  // slice 3): GET /api/animals/present (the dier-aanwezig feed) with live Naam/Chipnummer/Diersoort/
  // Ras filters derived from the present set. Being present IS the eligibility (inverted vs intake).
  // `size` is NOT optional here: the endpoint pages at 24 by default, so the grid showed the 24
  // newest animals and the Naam/Chip/Diersoort/Ras filters — which run over the loaded set, and which
  // also build their own option lists from it — could never reach the rest of the shelter. The picker
  // needs the whole present set; an opvang holds hundreds, not thousands.
  const { data: presentResp } = useCustom({
    url: "animals/present?size=1000",
    method: "get",
    queryOptions: { enabled: !locked },
  });
  const presentData = presentResp?.data as { items?: PresentAnimalCard[]; total?: number } | undefined;
  const presentAnimals: PresentAnimalCard[] = presentData?.items ?? [];
  // If a shelter ever outgrows that, say so instead of quietly showing a subset.
  const presentTruncated = (presentData?.total ?? 0) > presentAnimals.length;
  const [animalFilters, setAnimalFilters] = useState({ naam: "", chip: "", diersoort: "", ras: "" });
  // The cards showed nothing but camera placeholders: `fotoUrl` is the *online* image url, which is
  // null for every animal whose photo was uploaded in the shelter. Those bytes come from the
  // bearer-authenticated GET /api/animals/{id}/photo/content, so resolve them the same way the intake
  // results and the overview screens do.
  // Also the picked animals: on a launch from the UIT tile the present feed isn't fetched at all, so
  // the chosen animal had no entry here and its row fell back to the camera placeholder even though
  // the animal has a photo.
  const presentPhotoUrls = useAnimalPhotoUrls([
    ...presentAnimals.map((a) => a.id),
    ...selected.map((a) => a.id),
  ]);
  const photoOf = (id: number, fotoUrl: string | null): string | null =>
    presentPhotoUrls[String(id)] ?? fotoUrl;

  function toSelected(a: PresentAnimalCard): SelectedAnimal {
    const naam = a.naam ?? `#${a.id}`;
    return {
      id: a.id,
      label: `${naam}${a.ras ? ` (${a.ras})` : ""}${a.chipPasnummer ? ` · ${a.chipPasnummer}` : ""}`,
      chip: a.chipPasnummer,
      naam,
      soort: a.diersoort,
      ras: a.ras,
      geslacht: a.geslacht,
      kleur: a.kleur,
      vacht: a.vacht,
      fotoUrl: a.fotoUrl,
    };
  }

  // Seed the preselected animal once it arrives (launched from the UIT tile).
  useEffect(() => {
    if (!locked || selected.length > 0 || preselectedAnimal == null) return;
    const a = preselectedAnimal;
    setSelected([{
      id: a.id ?? Number(preselectedAnimalId),
      label: animalLabel(a),
      chip: a.chipPasnummer,
      naam: a.naam,
      soort: a.diersoort,
      ras: a.ras,
      geslacht: null,
      kleur: null,
      vacht: null,
      fotoUrl: null,
    }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked, preselectedAnimalId, preselectedAnimal, selected.length]);

  // Shared release moment (date). Euthanasia/deceased/nature edit it via their own date field; for
  // euthanasia/deceased it also stamps Dier.overledendatum server-side.
  const [releaseDate, setReleaseDate] = useState<string>(todayIso());

  // ADOPTION / RETURN_TO_OWNER — the new owner (klant_uit).
  const [newOwnerId, setNewOwnerId] = useState<number | null>(null);
  const [newOwnerLabel, setNewOwnerLabel] = useState<string | null>(null);
  // ADOPTION / RETURN_TO_OWNER blokkade override reason (only used when the new owner is on the
  // zwarte lijst + overrule role).
  const [overrideReden, setOverrideReden] = useState("");

  // ADOPTION economics (mirrors the surrender-intake path): cost lines + terms + payment. The lines
  // seed with the default Adoptiebijdrage (see the seed effect below) and grow via the catalog picker.
  const [adoptionCosts, setAdoptionCosts] = useState<CostLine[]>([]);
  const btwPercentages = useBtwPercentages();
  const [costSeq, setCostSeq] = useState(1);
  const adoptionCostsSeeded = useRef(false);
  // "Product of tarief toevoegen" catalog picker (Jmix FactuurRegelDialog picker mode).
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  // "Taak voor nazorg aanmaken" (Jmix AdoptionSummaryFragment nazorg card): default checked; shown —
  // and sent — only when the nazorg module is enabled, like the Jmix module gate.
  const [nazorgTask, setNazorgTask] = useState(true);
  const { isModuleEnabled } = useModuleAvailability();
  const nazorgModuleEnabled = isModuleEnabled("nazorg");

  const [adoptionTerms, setAdoptionTerms] = useState({
    accepted: false,
    signature: "",
    signedLater: false,
    date: todayIso(),
    place: "",
  });
  // De overeenkomst wordt op de vestiging getekend, dus "Plaats" begint daar (Jmix drukt
  // "Plaats: ENSCHEDE" af). Eén keer gevuld, en alleen zolang het veld nog leeg is.
  const { data: vestigingResp } = useCustom<{ plaats?: string | null }>({
    url: "vestigingen/current",
    method: "get",
  });
  const vestigingPlaats = vestigingResp?.data?.plaats ?? null;
  useEffect(() => {
    if (!vestigingPlaats) return;
    setAdoptionTerms((s) => (s.place ? s : { ...s, place: vestigingPlaats }));
    setReturnDeclaration((s) => (s.place ? s : { ...s, place: vestigingPlaats }));
  }, [vestigingPlaats]);
  const [adoptionPayment, setAdoptionPayment] = useState({ pin: "", cash: "", later: "", reference: "" });
  const [adoptionPayMethod, setAdoptionPayMethod] = useState<ReleasePayTile | null>(null);
  // M8: the on-screen agreement = the archived document. The server renders the SAME
  // plaatsingsovereenkomst template the ReleaseDocumentArchiver stores on completion, as page images
  // (Jmix AdoptionTermsFragment.embedPreviewPdf), refreshed (debounced) while the operator signs. An
  // empty page list falls back to the short summary text, mirroring the Jmix fragment's fallback.
  const [termsPreviewPages, setTermsPreviewPages] = useState<string[]>([]);

  // Tariff auto-proposal (Epic 16 / story 16-9 parity): the engine suggests an adoption fee from
  // species + process "Adoptie"; the operator confirms or overrides. Read-only — the POST is a query,
  // it never writes. The branch is resolved server-side.
  const { mutate: proposeTariff, isLoading: tariefBusy } = useCustomMutation<TariefVoorstelResponse>();
  const [tariefNotice, setTariefNotice] = useState<{ type: "ok" | "warn"; text: string } | null>(null);

  function proposeAdoptionTariff() {
    const first = selected[0];
    // The species rides along on the picked animal — no need to hunt it in a list.
    const diersoort = first?.soort ?? null;
    setTariefNotice(null);
    proposeTariff(
      // animalId lets the server fill the age (from the Dier's geboortedatum) so age-banded rules resolve.
      { url: "tarief-voorstel", method: "post", values: { proces: "Adoptie", diersoort, animalId: first?.id ?? null } },
      {
        onSuccess: ({ data }) => applyTariefVoorstel(data),
        onError: () => setTariefNotice({ type: "warn", text: t("inboeken.costs.propose.error") }),
      },
    );
  }

  function applyTariefVoorstel(r: TariefVoorstelResponse) {
    if (r.status === "MATCHED" && r.bedrag != null) {
      const omschrijving = r.omschrijving ?? r.regelNaam ?? "Tarief";
      setAdoptionCosts((cur) => [
        ...cur,
        { id: costSeq, description: omschrijving, amount: r.bedrag!.toFixed(2), btw: r.btwPercentage ?? null },
      ]);
      setCostSeq((n) => n + 1);
      setTariefNotice({
        type: "ok",
        text: t("inboeken.costs.propose.matched", { args: [omschrijving, formatEuro(r.bedrag, locale)] }),
      });
    } else if (r.status === "MATCHED") {
      setTariefNotice({ type: "warn", text: t("inboeken.costs.propose.noAmount") });
    } else if (r.status === "MANUAL_QUOTE") {
      setTariefNotice({ type: "warn", text: t("inboeken.costs.propose.manual") });
    } else if (r.status === "CONFLICT") {
      setTariefNotice({ type: "warn", text: t("inboeken.costs.propose.conflict", { args: [r.conflictRegels.join(", ")] }) });
    } else {
      setTariefNotice({ type: "warn", text: t("inboeken.costs.propose.none") });
    }
  }

  // RETURN_TO_OWNER — details remark + cost lines (seeded opvangkosten + ambulance/vet/admin) + payment.
  // Unlike adoption, return-to-owner generates NO concept-factuur (matches Jmix); the cost lines feed the
  // signed retourverklaring + a desk payment, captured in the WizardData JSON for audit only.
  const [returnDetails, setReturnDetails] = useState({ remark: "" });
  const [returnCosts, setReturnCosts] = useState<CostLine[]>([]);
  // Een regel met een bedrag maar zonder BTW houdt de kostenstap tegen (0% mag, maar moet gekozen zijn).
  const adoptionBtwOntbreekt = kostenregelZonderBtw(adoptionCosts, parseAmount);
  const returnBtwOntbreekt = kostenregelZonderBtw(returnCosts, parseAmount);
  const [returnCostSeq, setReturnCostSeq] = useState(1000);
  const [returnPayment, setReturnPayment] = useState({ pin: "", cash: "", later: "", reference: "" });
  const [returnPayMethod, setReturnPayMethod] = useState<ReleasePayTile | null>(null);
  // Source of the chosen original owner: pre-suggested from the intake relation, or manually searched.
  const [returnOwnerSource, setReturnOwnerSource] = useState<string | null>(null);
  // Set when the operator rejects the intake-relation suggestion ("search another owner"), so the
  // auto-suggest effect doesn't immediately re-apply it.
  // Retour: het antwoord op "gaat het dier terug naar deze eigenaar?" — null zolang er niets is
  // aangeklikt, precies als bij een plaatsing.
  const [returnOwnerAnswer, setReturnOwnerAnswer] = useState<"yes" | "no" | null>(null);
  // Plaatsing: het antwoord op "wordt het dier bij deze eigenaar geplaatst?". null = nog niet
  // beantwoord. Anders dan bij retour eigenaar wordt hier NIETS vooringevuld: een plaatsing gaat
  // meestal juist naar iemand anders, en een stilzwijgend overgenomen eigenaar zou een dier bij zijn
  // vorige baas kunnen boeken zonder dat iemand het bewust koos.
  // De eigenaarstap draait het volledige Jmix-zoekscherm (e-mail / adres / nieuwe relatie), net als de
  // inboek-wizard. Dat scherm draagt zijn eigen Terug/Zoeken-knoppen, dus de modalvoet stapt opzij
  // zolang het zoekt — twee keer Terug onder elkaar is onzin.
  const [relationPhase, setRelationPhase] = useState<"find" | "results" | "details" | "edit">("find");
  // Gezet zodra de picker een relatie heeft aangemaakt; de effect hieronder stapt door zodra die
  // relatie ook echt in de state staat.
  const [naAanmakenDoor, setNaAanmakenDoor] = useState(false);
  // "Bevestigen" bewaart wat er in het detailformulier van de gekozen relatie is bijgewerkt; de picker
  // meldt hier hoe dat moet (geen aparte opslaan-knop, zoals in de inboek-wizard).
  const relationSaveRef = useRef<(() => void) | null>(null);
  const registerRelationSave = useCallback((fn: (() => void) | null) => {
    relationSaveRef.current = fn;
  }, []);
  const returnCostsSeeded = useRef(false);
  const [returnDeclaration, setReturnDeclaration] = useState({
    accepted: false,
    signature: "",
    date: todayIso(),
    place: "",
  });

  // EUTHANASIA
  const [euthanasia, setEuthanasia] = useState({
    performer: EUTHANASIA_PERFORMER_INTERNAL,
    vetName: "",
    vetPractice: "",
    reason: "",
    remarks: "",
  });

  // DECEASED — externalVet mirrors the Jmix "Was er een externe dierenarts betrokken?" radio
  // (default Nee); the vet name/reference fields only apply — and the name is only required —
  // when it is Ja (DeceasedDetailsFragment).
  const [deceased, setDeceased] = useState({
    cause: "",
    causeOther: "",
    externalVet: false,
    vetName: "",
    vetReference: "",
    remarks: "",
  });

  // NATURE_RELEASE
  const [nature, setNature] = useState({
    gemeenteId: "",
    city: "",
    postalCode: "",
    houseNumber: "",
    houseNumberAddition: "",
    street: "",
    remark: "",
    vetCheck: false,
  });
  const [natureGeo, setNatureGeo] = useState<{ lat: number | null; lng: number | null }>({ lat: null, lng: null });
  // Where the animal is being released: back at the intake's found/caught spot, or somewhere else.
  // A TNRC cat goes back where it was trapped, so "found" is the default whenever the intake recorded
  // a location; without one there is nothing to choose and the step is the plain address form.
  const [naturePlace, setNaturePlace] = useState<"found" | "other">("found");
  // The found location is copied into the fields once (per animal), so the operator can still correct
  // it — copying on every render would fight every keystroke.
  const naturePrefilled = useRef<number | null>(null);

  // EXCHANGE — the receiving shelter (an ASIEL klant; reuses newOwnerId as the target) + a reason.
  const [exchangeReason, setExchangeReason] = useState("");
  const eligibleShelters = useFeed<ExchangeShelter>("release/reference/eligible-shelters", option === EXCHANGE);

  const gemeenten = useFeed<OptionRow>("release/reference/gemeenten", option === NATURE_RELEASE);

  // Nature-release location geocoding (verblijf-wizard parity): forward (address → pin) + reverse (map
  // click → address fields) via the api/ proxy, mirroring the InboekenWizard found-location step.
  const natureGemeenteLabel = gemeenten.find((g) => String(g.id) === nature.gemeenteId)?.label ?? "";
  // Default map center = the current shelter's own geocoded address (Jmix parity), used until a pin exists.
  const natureMapCenter = useVestigingMapCenter(option === NATURE_RELEASE);

  /** Copy the intake's found location into the uitzet-address fields (and drop the pin on it). */
  function fillFromFoundLocation(loc: IntakeFoundLocation) {
    setNature((s) => ({
      ...s,
      gemeenteId: loc.gemeenteId != null ? String(loc.gemeenteId) : "",
      city: loc.woonplaats ?? "",
      postalCode: loc.postcode ?? "",
      houseNumber: loc.huisnummer ?? "",
      houseNumberAddition: loc.huisnummerToevoeging ?? "",
      street: loc.straat ?? "",
    }));
    if (loc.latitude != null && loc.longitude != null) {
      setNatureGeo({ lat: Number(loc.latitude), lng: Number(loc.longitude) });
    }
  }

  const { handlePick: handleNaturePick } = useFoundLocationGeocoding({
    enabled: option === NATURE_RELEASE,
    address: {
      street: nature.street,
      houseNumber: nature.houseNumber,
      houseNumberAddition: nature.houseNumberAddition,
      postalCode: nature.postalCode,
      city: nature.city,
      municipality: natureGemeenteLabel,
    },
    onResolvePin: (la, lo) => setNatureGeo({ lat: la, lng: lo }),
    onReversePopulate: (addr) => {
      setNature((s) => {
        // Gemeente via the Jmix cascade (name → woonplaats → city): Nominatim's `municipality` is
        // regularly not a gemeente name at all, and the field then stayed empty after a map click.
        const gemeente = resolveGemeenteId(gemeenten, addr);
        return {
          ...s,
          city: addr.city ?? s.city,
          postalCode: addr.postalCode ?? s.postalCode,
          street: addr.street ?? s.street,
          houseNumber: addr.houseNumber ?? s.houseNumber,
          houseNumberAddition: addr.houseNumberAddition ?? s.houseNumberAddition,
          gemeenteId: gemeente != null ? String(gemeente) : s.gemeenteId,
        };
      });
    },
  });

  // PetBase chip-registration capture (Epic 4): one draft per chipped released animal, keyed by id.
  const [chipDraft, setChipDraft] = useState<Record<number, ReleaseChipCapture>>({});
  // Animals whose paspoort/kenmerken have already been seeded from their /animals/{id} detail — parent-
  // deduped so a card remount (navigating back to the step) can't clobber the operator's later edits.
  const detailSeeded = useRef<Set<number>>(new Set());

  // Pending type switch awaiting the "Vertreksoort wijzigen?" confirmation (Jmix ConfirmDialog) —
  // only armed when a different type is picked while later steps already hold operator input.
  const [pendingTypeId, setPendingTypeId] = useState<string | null>(null);

  function resetDetails() {
    setReleaseDate(todayIso());
    setNewOwnerId(null);
    setNewOwnerLabel(null);
    setAdoptionCosts([]);
    adoptionCostsSeeded.current = false;
    setNazorgTask(true);
    setAdoptionTerms({ accepted: false, signature: "", signedLater: false, date: todayIso(), place: "" });
    setAdoptionPayment({ pin: "", cash: "", later: "", reference: "" });
    setReturnDetails({ remark: "" });
    setReturnCosts([]);
    setReturnPayment({ pin: "", cash: "", later: "", reference: "" });
    setReturnOwnerSource(null);
    setReturnOwnerAnswer(null);
    returnCostsSeeded.current = false;
    setReturnDeclaration({ accepted: false, signature: "", date: todayIso(), place: "" });
    setEuthanasia({ performer: EUTHANASIA_PERFORMER_INTERNAL, vetName: "", vetPractice: "", reason: "", remarks: "" });
    setDeceased({ cause: "", causeOther: "", externalVet: false, vetName: "", vetReference: "", remarks: "" });
    setNature({ gemeenteId: "", city: "", postalCode: "", houseNumber: "", houseNumberAddition: "", street: "", remark: "", vetCheck: false });
    setNatureGeo({ lat: null, lng: null });
    setExchangeReason("");
  }

  // Submit mutation ONLY — `busy` gates the advance/finish buttons. The draft lifecycle gets its OWN
  // mutation hook: sharing one hook let the mount-time draft POST hold `busy` true (under StrictMode's
  // double-invoked effect the observer could stick in `loading` forever, permanently disabling
  // Volgende), and a slow autosave PATCH must never lock navigation either.
  const { mutate, isLoading: busy } = useCustomMutation();
  const { mutate: draftMutate } = useCustomMutation();

  // ── generic WizardData draft lifecycle (ADR adr-wizard-data-generic-store) ───────────────────────
  const [draftId, setDraftId] = useState<string | null>(null);
  const draftRequested = useRef(false);
  useEffect(() => {
    if (draftRequested.current) return;
    draftRequested.current = true;
    draftMutate(
      { url: "wizard", method: "post", values: { wizardType: "RELEASE" } },
      { onSuccess: (res) => setDraftId(((res as { data?: { id?: string } })?.data?.id) ?? null) },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function persistDraft() {
    if (!draftId) return;
    draftMutate({ url: `wizard/${draftId}`, method: "patch", values: { data: buildValues() } }, {});
  }

  // "Aanwezige dieren" grid: live filters over the present set (Jmix release-mode search); the
  // Diersoort/Ras filter options derive from the set itself, like the Jmix comboboxes.
  const filteredPresent = useMemo(() => {
    const naam = animalFilters.naam.trim().toLowerCase();
    const chip = animalFilters.chip.replace(/\s/g, "");
    return presentAnimals.filter((a) => {
      if (naam && !(a.naam ?? "").toLowerCase().includes(naam)) return false;
      if (chip && !(a.chipPasnummer ?? "").replace(/\s/g, "").includes(chip)) return false;
      if (animalFilters.diersoort && a.diersoort !== animalFilters.diersoort) return false;
      if (animalFilters.ras && a.ras !== animalFilters.ras) return false;
      return true;
    });
  }, [presentAnimals, animalFilters]);
  const presentSoorten = useMemo(
    () => [...new Set(presentAnimals.map((a) => a.diersoort).filter((v): v is string => !!v))].sort(),
    [presentAnimals],
  );
  const presentRassen = useMemo(
    () => [...new Set(presentAnimals.map((a) => a.ras).filter((v): v is string => !!v))].sort(),
    [presentAnimals],
  );

  const ownerLabelKey = option === RETURN_TO_OWNER ? "uitboeken.field.owner" : "uitboeken.field.newOwner";

  // Adoption cost total + payment running totals (mirror the surrender path).
  const adoptionRegelTotal = adoptionCosts.reduce((sum, c) => sum + (parseAmount(c.amount) ?? 0), 0);
  // Korting staat per regel: de btw van de korting is dan die van de regel zelf, en je kunt op het
  // ene product wel korting geven en op het andere niet. Nooit meer dan de regel waar hij op zit --
  // een negatieve regel is geen korting maar een terugbetaling, en die hoort via crediteren.
  const adoptionKortingBedrag = kortingTotaal(adoptionCosts);
  const adoptionKortingTeHoog = adoptionCosts.some(kortingRegelTeHoog);
  const adoptionCostTotal = afgerondeEuro(adoptionRegelTotal - adoptionKortingBedrag);
  const adoptionPaidNow = (parseAmount(adoptionPayment.pin) ?? 0) + (parseAmount(adoptionPayment.cash) ?? 0);
  // Total registered incl. the deferred "later" method — used only for the "at least one payment"
  // advance-gate (Jmix methodRequired), so a €0-due release can still pass with nothing registered.
  const adoptionPaidTotal = adoptionPaidNow + (parseAmount(adoptionPayment.later) ?? 0);
  // Wat er wel en niet mag: dezelfde regel als bij inboeken, dus staat hij in betalingRegels.
  // Kort: dezelfde richting als het totaal (een negatief bedrag alleen bij een negatieve factuur),
  // niet meer dan het totaal, en een restant mag maar moet bevestigd worden.
  const adoptionOordeel = betalingOordeel(adoptionCostTotal, adoptionPaidTotal);
  const adoptionTeveel = adoptionOordeel.fout !== null;

  // Return-to-owner cost total + paid-now (same running totals, for the cost step / payment / summary).
  const returnRegelTotal = returnCosts.reduce((sum, c) => sum + (parseAmount(c.amount) ?? 0), 0);
  const returnKortingBedrag = kortingTotaal(returnCosts);
  const returnKortingTeHoog = returnCosts.some(kortingRegelTeHoog);
  const returnCostTotal = afgerondeEuro(returnRegelTotal - returnKortingBedrag);
  const returnPaidNow = (parseAmount(returnPayment.pin) ?? 0) + (parseAmount(returnPayment.cash) ?? 0);
  const returnPaidTotal = returnPaidNow + (parseAmount(returnPayment.later) ?? 0);
  const returnOordeel = betalingOordeel(returnCostTotal, returnPaidTotal);
  const returnTeveel = returnOordeel.fout !== null;

  // Active stay of the (first) released animal — backs the owner pre-suggestion + opvangkosten day-count
  // + the stay card. Mirrors the Jmix ReturnToOwnerDetailsFragment.loadActiveVerblijf / resolveIntakeRelation.
  const firstSelectedId = selected[0]?.id ?? null;
  // A nature release needs the same row for its found-location option ("uitzetten op de vindplaats"),
  // so the query is no longer RETURN-only.
  const stayQuery = useCustom<IntakeStay>({
    url: `release/reference/animals/${firstSelectedId}/intake-stay`,
    method: "get",
    queryOptions: {
      enabled: (option === RETURN_TO_OWNER || option === NATURE_RELEASE) && firstSelectedId != null,
    },
  });
  const stayResponse = stayQuery.data?.data ?? null;
  // Only THIS animal's stay counts. The query is keyed on the animal, so between picking another
  // animal and its response landing, `data` still holds the previous one — and the prefill below runs
  // exactly once per animal, so a stale row would decide the answer for good.
  const activeStay =
    stayResponse != null && (stayResponse.animalId == null
      || Number(stayResponse.animalId) === Number(firstSelectedId))
      ? stayResponse
      : null;
  const returnStay = option === RETURN_TO_OWNER ? activeStay : null;

  // De bij het dier bekende eigenaar — dezelfde server-side keten als de inboek-wizard gebruikt voor
  // "Eigenaar controleren" (de geregistreerde eigenaar, anders die van het laatste verblijf). Bij een
  // plaatsing is dat de kandidaat waarover we de vraag stellen, niet een ingevulde waarde. 404 = niets
  // bekend; dan valt er niets te vragen en gaat de stap meteen zoeken.
  const knownOwnerQuery = useCustom({
    url: `release/reference/animals/${firstSelectedId}/known-owner`,
    method: "get",
    queryOptions: { enabled: (option === ADOPTION || option === RETURN_TO_OWNER) && firstSelectedId != null },
  });
  const knownOwnerData = knownOwnerQuery.data?.data;
  const bekendeEigenaar =
    knownOwnerData && !Array.isArray(knownOwnerData) ? (knownOwnerData as KnownOwnerView) : null;
  // Alleen bij een retour is de bekende eigenaar een kandidaat: daar gáát het dier naar hem terug.
  // Bij een plaatsing is hij juist degene die het dier heeft afgestaan.
  const returnKnownOwner = option === RETURN_TO_OWNER ? bekendeEigenaar : null;
  // Retour eigenaar: de stap toont de voorgestelde eigenaar (en niet het zoekscherm) zolang die
  // suggestie staat. Hier afgeleid en niet in de render, omdat stepDefs hem ook nodig heeft — de
  // modalvoet stapt opzij zodra het zoekscherm zijn eigen Terug/Zoeken draagt.
  const returnAsksOwner = returnOwnerAnswer !== "no" && returnKnownOwner?.ownerId != null;
  const intakeFoundLocation = option === NATURE_RELEASE ? (activeStay?.foundLocation ?? null) : null;

  // Seed the found location once the stay has actually ARRIVED, and again whenever another animal is
  // picked. Deliberately not gated on `isLoading`: a query that is merely disabled (the option isn't
  // TNRC yet) reports isLoading=false, so the effect fired before the request was even sent, concluded
  // "geen vindplaats", and latched that — the fields and the map stayed empty for an animal whose
  // intake DID record a location.
  useEffect(() => {
    if (option !== NATURE_RELEASE || firstSelectedId == null) return;
    if (naturePrefilled.current === firstSelectedId) return;
    if (stayQuery.isFetching) return;
    // Nothing settled yet (no row for this animal and no error) → wait; don't decide on absence.
    if (activeStay == null && !stayQuery.isError) return;
    naturePrefilled.current = firstSelectedId;
    if (intakeFoundLocation) {
      setNaturePlace("found");
      fillFromFoundLocation(intakeFoundLocation);
    } else {
      // Nothing recorded on intake → nothing to prefill, so start on the free form. The question is
      // still asked; the operator can flip it back to "op de vangplek" and type the address.
      setNaturePlace("other");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option, firstSelectedId, stayQuery.isFetching, stayQuery.isError, activeStay, intakeFoundLocation]);

  // Number of days the animal stayed — INCLUSIVE count, both the intake day and the return day
  // count (Jmix ReturnToOwnerDetailsFragment: ChronoUnit.DAYS.between + 1, "4 juni → 5 juni = 2
  // dagen"), floored at 1. Falls back to 1 day when the intake date is unknown.
  const returnDays = useMemo(() => {
    if (!returnStay?.intakeDate) return 1;
    const inDate = new Date(`${returnStay.intakeDate}T00:00:00`);
    const outDate = new Date(`${releaseDate || todayIso()}T00:00:00`);
    const diff = Math.round((outDate.getTime() - inDate.getTime()) / 86_400_000);
    return Math.max(1, diff + 1);
  }, [returnStay, releaseDate]);

  // Seed the default return cost lines ONCE per return flow, after the stay query settles so the
  // opvangkosten day-count is known. Mirrors ReturnToOwnerCostLinesStateService.buildDefaultLines:
  // Opvangkosten (days × €10) + Dierenambulance/Dierenarts/Administratiekosten at €0. All editable;
  // the defaults are fixed (no remove), like the Jmix non-deletable default lines.
  useEffect(() => {
    if (option !== RETURN_TO_OWNER || returnCostsSeeded.current) return;
    // Wait for an animal to be picked and its stay to settle, so the opvangkosten day-count is correct
    // (option flips at the type step, before animal selection — don't seed a stale 1-day line then).
    if (firstSelectedId == null || stayQuery.isLoading) return;
    returnCostsSeeded.current = true;
    const dagen = returnDays === 1 ? t("uitboeken.returnCosts.day") : t("uitboeken.returnCosts.days");
    const opvangLabel = `${t("uitboeken.returnCosts.opvang")} (${returnDays} ${dagen} × ${formatEuro(RETURN_DAILY_RATE, locale)})`;
    setReturnCosts([
      { id: 1, description: opvangLabel, amount: String(returnDays * RETURN_DAILY_RATE), fixed: true },
      { id: 2, description: t("uitboeken.returnCosts.ambulance"), amount: "", fixed: true },
      { id: 3, description: t("uitboeken.returnCosts.vet"), amount: "", fixed: true },
      { id: 4, description: t("uitboeken.returnCosts.admin"), amount: "", fixed: true },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option, firstSelectedId, stayQuery.isLoading]);

  // Seed the adoption cost lines ONCE per adoption flow with the default base fee, then run the tariff
  // engine automatically and SILENTLY replace the placeholder when it matches — mirroring the Jmix
  // AdoptionCostsFragment (setupData seeds Adoptiebijdrage €100; vulTariefAutomatischIn swaps it for
  // the engine's line, and only while the operator hasn't touched the lines yet). The manual "Tarief
  // voorstellen" button stays. Waits for an animal so the species-banded rules can resolve.
  useEffect(() => {
    if (option !== ADOPTION || adoptionCostsSeeded.current) return;
    if (firstSelectedId == null) return;
    adoptionCostsSeeded.current = true;
    setAdoptionCosts([{ id: 0, description: ADOPTION_DEFAULT_PRODUCT, amount: ADOPTION_DEFAULT_AMOUNT.toFixed(2) }]);
    const diersoort = selected[0]?.soort ?? null;
    proposeTariff(
      { url: "tarief-voorstel", method: "post", values: { proces: "Adoptie", diersoort, animalId: firstSelectedId } },
      {
        onSuccess: ({ data }) => {
          if (data.status !== "MATCHED" || data.bedrag == null) return; // silent, like Jmix
          const omschrijving = data.omschrijving ?? data.regelNaam ?? "Tarief";
          setAdoptionCosts((cur) =>
            cur.length === 1 && isDefaultAdoptionLine(cur[0])
              ? [{ id: 0, description: omschrijving, amount: data.bedrag!.toFixed(2), btw: data.btwPercentage ?? null }]
              : cur,
          );
        },
        onError: () => {}, // silent — the auto-run never bothers the operator (Jmix parity)
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option, firstSelectedId]);

  // Tariff auto-proposal for return-to-owner: the engine prices the boarding (opvangkosten) per day
  // (proces "Retour eigenaar", PER_DAY / ASIEL_RETOUR_DAGPRIJS) from the stay length — replacing the
  // hardcoded RETURN_DAILY_RATE seed. animalId lets the server fill species + age from the Dier.
  function proposeReturnTariff() {
    setTariefNotice(null);
    proposeTariff(
      {
        url: "tarief-voorstel",
        method: "post",
        values: { proces: "Retour eigenaar", animalId: firstSelectedId, aantalDagen: returnDays },
      },
      {
        onSuccess: ({ data }) => applyReturnTariefVoorstel(data),
        onError: () => setTariefNotice({ type: "warn", text: t("inboeken.costs.propose.error") }),
      },
    );
  }

  function applyReturnTariefVoorstel(r: TariefVoorstelResponse) {
    if (r.status === "MATCHED" && r.bedrag != null) {
      const omschrijving = r.omschrijving ?? r.regelNaam ?? t("uitboeken.returnCosts.opvang");
      const line: CostLine = {
        id: 1,
        description: omschrijving,
        amount: r.bedrag.toFixed(2),
        fixed: true,
        btw: r.btwPercentage ?? null,
      };
      // Replace the seeded boarding line (id 1) with the engine's per-day proposal; keep it non-removable.
      setReturnCosts((cur) =>
        cur.some((c) => c.id === 1) ? cur.map((c) => (c.id === 1 ? line : c)) : [line, ...cur],
      );
      setTariefNotice({
        type: "ok",
        text: t("inboeken.costs.propose.matched", { args: [omschrijving, formatEuro(r.bedrag, locale)] }),
      });
    } else if (r.status === "MATCHED") {
      setTariefNotice({ type: "warn", text: t("inboeken.costs.propose.noAmount") });
    } else if (r.status === "MANUAL_QUOTE") {
      setTariefNotice({ type: "warn", text: t("inboeken.costs.propose.manual") });
    } else if (r.status === "CONFLICT") {
      setTariefNotice({ type: "warn", text: t("inboeken.costs.propose.conflict", { args: [r.conflictRegels.join(", ")] }) });
    } else {
      setTariefNotice({ type: "warn", text: t("inboeken.costs.propose.none") });
    }
  }


  // Blokkade guard: when the selected new owner is on the zwarte lijst, completion is blocked unless
  // the user may overrule it and supplies a reason (≥10 chars), logged server-side. Mirrors the Jmix
  // BlokkadeAdoptieGuard, which guards BOTH the adoption step and the return-to-owner path
  // (ReturnToOwnerRelationFragment.guardAndProceed) — EXCHANGE goes to a shelter, not a person.
  const blokkadeGuarded = option === ADOPTION || option === RETURN_TO_OWNER;
  const blokkadeCheck = useCustom<{ actief: { redenLabel: string | null } | null; canOverrule: boolean }>({
    url: `release/reference/klanten/${newOwnerId}/blokkade`,
    method: "get",
    queryOptions: { enabled: blokkadeGuarded && newOwnerId != null },
  });
  const actiefBlokkade = blokkadeGuarded && newOwnerId != null ? (blokkadeCheck.data?.data?.actief ?? null) : null;
  const canOverrule = blokkadeCheck.data?.data?.canOverrule ?? false;
  const blokkadeBlocked =
    actiefBlokkade != null && (!canOverrule || overrideReden.trim().length < 10);

  // The chosen relation's full contact details for the summary's eigenaar/organisatie tile (Jmix
  // Adoption/ReturnToOwner/Exchange summary fragments show naam + adres + e-mail + telefoon). The
  // RelationContactPanel on the declaration step fetches the same url — React Query dedupes.
  const { data: ownerDetailResp } = useCustom({
    url: `release/reference/klanten/${newOwnerId}`,
    method: "get",
    queryOptions: {
      enabled:
        newOwnerId != null && (option === ADOPTION || option === RETURN_TO_OWNER || option === EXCHANGE),
    },
  });
  const ownerDetail = (ownerDetailResp?.data as KlantDetail | undefined) ?? null;

  function buildValues(): Record<string, unknown> {
    const values: Record<string, unknown> = {
      asielVertrekSoortId: soortId ? Number(soortId) : null,
      releaseDateTime: releaseDate ? `${releaseDate}T00:00:00` : null,
      animals: selected.map((a) => ({ animalId: a.id })),
      // EXCHANGE reuses newOwnerId as the receiving shelter (klant); the server creates + sends the
      // shelter_exchange to it on completion.
      newOwnerId: option === ADOPTION || option === RETURN_TO_OWNER || option === EXCHANGE ? newOwnerId : null,
      blokkadeOverrideReden: blokkadeGuarded ? overrideReden.trim() || null : null,
    };
    if (option === ADOPTION) {
      // economics — session/audit data (WizardData JSON only; the concept-factuur is generated
      // server-side on completion from the cost lines + paid amount, keyed on the new owner).
      values.adoptionCostLines = adoptionCosts.map((c) => ({
        description: c.description || null,
        amount: parseAmount(c.amount),
        btwPercentage: c.btw ?? null,
        korting: kortingVanRegel(c) || null,
      }));
      values.adoptionCostTotal = adoptionCostTotal;
      values.adoptionTermsAccepted = adoptionTerms.accepted;
      values.adoptionTermsSignature = adoptionTerms.signature || null;
      values.adoptionTermsSignedLater = adoptionTerms.signedLater;
      values.adoptionTermsDate = adoptionTerms.date || null;
      values.adoptionTermsPlace = adoptionTerms.place || null;
      values.adoptionPaymentPin = parseAmount(adoptionPayment.pin);
      values.adoptionPaymentCash = parseAmount(adoptionPayment.cash);
      values.adoptionPaymentLater = parseAmount(adoptionPayment.later);
      values.adoptionPaymentReference = adoptionPayment.reference || null;
      // "Taak voor nazorg aanmaken" (M6): explicit opt-out for the server's nazorg auto-issuance —
      // false when unticked OR when the nazorg module is off (Jmix isNazorgTaskRequested semantics).
      values.nazorgTaskRequested = nazorgModuleEnabled && nazorgTask;
    } else if (option === RETURN_TO_OWNER) {
      values.returnDetailsRemark = returnDetails.remark || null;
      // Cost lines + payment — captured for the signed retourverklaring + audit (WizardData JSON only;
      // unlike adoption, return-to-owner deliberately creates NO concept-factuur, matching Jmix).
      values.returnCostLines = returnCosts.map((c) => ({
        description: c.description || null,
        amount: parseAmount(c.amount),
        btwPercentage: c.btw ?? null,
        korting: kortingVanRegel(c) || null,
      }));
      values.returnCostTotal = returnCostTotal;
      values.returnPaymentPin = parseAmount(returnPayment.pin);
      values.returnPaymentCash = parseAmount(returnPayment.cash);
      values.returnPaymentLater = parseAmount(returnPayment.later);
      values.returnPaymentReference = returnPayment.reference || null;
      values.returnOriginalOwnerSource = returnOwnerSource;
      values.returnDeclarationAccepted = returnDeclaration.accepted;
      values.returnDeclarationSignature = returnDeclaration.signature || null;
      values.returnDeclarationDate = returnDeclaration.date || null;
      values.returnDeclarationPlace = returnDeclaration.place || null;
      values.opmerkingUit = returnDetails.remark || null;
    } else if (option === EUTHANASIA) {
      values.euthanasiaPerformer = euthanasia.performer || null;
      values.euthanasiaVetName = euthanasia.performer === EUTHANASIA_PERFORMER_EXTERNAL ? euthanasia.vetName || null : null;
      values.euthanasiaVetPractice = euthanasia.performer === EUTHANASIA_PERFORMER_EXTERNAL ? euthanasia.vetPractice || null : null;
      values.euthanasiaReason = euthanasia.reason || null;
      values.euthanasiaRemarks = euthanasia.remarks || null;
      values.opmerkingUit = euthanasia.remarks || null;
    } else if (option === DECEASED) {
      values.deceasedCause = deceased.cause === DECEASED_CAUSE_OTHER ? deceased.causeOther || DECEASED_CAUSE_OTHER : deceased.cause || null;
      // Jmix semantics: an empty external_vet means "no external vet involved" — only send the
      // name/reference when the operator answered Ja on the external-vet question.
      values.deceasedVetName = deceased.externalVet ? deceased.vetName || null : null;
      values.deceasedVetReference = deceased.externalVet ? deceased.vetReference || null : null;
      values.deceasedRemarks = deceased.remarks || null;
      values.opmerkingUit = deceased.remarks || null;
    } else if (option === NATURE_RELEASE) {
      // The whole TNRC release travels as ONE object, because the server binds this JSON straight onto
      // CreateReleaseRequest and stores it as a row (nature_release) — the flat keys stayed in the draft
      // and could never be counted. `atFoundLocation` is the field the overviews group on.
      values.natureRelease = {
        atFoundLocation: naturePlace === "found",
        straat: nature.street || null,
        huisnummer: nature.houseNumber || null,
        huisnummerToevoeging: nature.houseNumberAddition || null,
        postcode: nature.postalCode || null,
        woonplaats: nature.city || null,
        gemeenteId: nature.gemeenteId ? Number(nature.gemeenteId) : null,
        latitude: natureGeo.lat,
        longitude: natureGeo.lng,
        opmerking: nature.remark || null,
        dierenartsCheck: nature.vetCheck,
      };
      values.opmerkingUit = nature.remark || null;
    } else if (option === EXCHANGE) {
      // reason → WizardData JSON; the server reads it when creating + sending the shelter_exchange.
      values.exchangeReason = exchangeReason.trim() || null;
    }
    // PetBase chip captures (Epic 4) — one per chipped animal. The server reads these from the WizardData
    // JSON and enqueues a RELEASE job per chipped animal on /complete (CreateReleaseRequest ignores them);
    // a chipless animal carries no capture and is auto-skipped (SKIPPED_NO_CHIP) server-side.
    if (showPetbaseChipStep) {
      values.chipCaptures = chippedAnimals.map((a) => {
        const d = chipDraft[a.id];
        return {
          animalIndex: selected.findIndex((s) => s.id === a.id),
          chipNumber: d?.chipNumber?.trim() || null,
          chipRegistered: d?.chipRegistered ?? false,
          passportNumber: d?.passportNumber?.trim() || null,
          kenmerken: d?.kenmerken?.trim() || null,
        };
      });
    }
    return values;
  }

  /** Klaar: het scherm gaat naar het dier dat zojuist is uitgeboekt. */
  function finishWithFeedback(_resp: unknown) {
    onDone(selected.map((a) => a.id));
  }

  function submit() {
    setNotice(null);
    const values = buildValues();
    if (draftId) {
      mutate(
        { url: `wizard/${draftId}`, method: "patch", values: { data: values } },
        {
          onSuccess: () =>
            mutate(
              { url: `wizard/${draftId}/complete`, method: "post", values: {} },
              { onSuccess: finishWithFeedback, onError: (err) => setNotice(noticeFor(err)) },
            ),
          onError: (err) => setNotice(noticeFor(err)),
        },
      );
    } else {
      // No draft yet (its opening POST lost a race with a fast finish). POST /release would complete
      // the departure, but it takes only the domain fields — the terms acceptance and the SIGNATURE
      // live solely in the draft JSON, so the signed plaatsingsovereenkomst would never be archived
      // and nobody would notice. Open a draft now and finish through it. Same fix as the intake
      // wizard; that path is where this was first found.
      mutate(
        { url: "wizard", method: "post", values: { wizardType: "RELEASE" } },
        {
          onSuccess: (res) => {
            const id = (res as { data?: { id?: string } })?.data?.id ?? null;
            if (!id) {
              setNotice("error");
              return;
            }
            setDraftId(id);
            mutate(
              { url: `wizard/${id}`, method: "patch", values: { data: values } },
              {
                onSuccess: () =>
                  mutate(
                    { url: `wizard/${id}/complete`, method: "post", values: {} },
                    { onSuccess: finishWithFeedback, onError: (err) => setNotice(noticeFor(err)) },
                  ),
                onError: (err) => setNotice(noticeFor(err)),
              },
            );
          },
          onError: (err) => setNotice(noticeFor(err)),
        },
      );
    }
  }

  // Per-type advance gates (mirror the Jmix validateBeforeParentAdvance checks).
  const adoptionTermsBlocked =
    !adoptionTerms.accepted || (!adoptionTerms.signedLater && !adoptionTerms.signature);
  const returnDeclarationBlocked = !returnDeclaration.accepted;
  const euthanasiaExternal = euthanasia.performer === EUTHANASIA_PERFORMER_EXTERNAL;
  const euthanasiaBlocked =
    !releaseDate || !euthanasia.performer || !euthanasia.reason || (euthanasiaExternal && !euthanasia.vetName.trim());
  const deceasedBlocked =
    !releaseDate ||
    !deceased.cause ||
    (deceased.cause === DECEASED_CAUSE_OTHER && !deceased.causeOther.trim()) ||
    (deceased.externalVet && !deceased.vetName.trim());
  const natureBlocked = !releaseDate;
  // Jmix ExchangeDetailsFragment.isValid(): the exchange date AND a non-blank reason are required.
  const exchangeBlocked = !releaseDate || !exchangeReason.trim();

  // ── PetBase chip step (Epic 4) ────────────────────────────────────────────────────────────────────
  // Selected animals that carry a chip — only these get a capture card (chipless animals are auto-skipped
  // server-side as SKIPPED_NO_CHIP). The chip number comes from the animals feed (SelectedAnimal.chip).
  const chippedAnimals = useMemo(() => selected.filter((a) => (a.chip ?? "").trim() !== ""), [selected]);
  // FR18: the step appears for every release option with a chipped animal (Story 4.2) — but only when
  // PetBase is configured for the vestiging, mirroring the Jmix isPetbaseConfigured() gate (and the
  // intake wizard's identifiers probe). Unconfigured → the step is hidden entirely and no captures are
  // sent, so the server enqueues nothing.
  const { data: releasePetbaseResp } = useCustom({
    url: "release/petbase/identifiers",
    method: "get",
    queryOptions: { enabled: chippedAnimals.length > 0 },
  });
  const releasePetbaseData = releasePetbaseResp?.data as { configured?: boolean } | undefined;
  const releasePetbaseConfigured = releasePetbaseData?.configured === true;
  // Death flow (euthanasie/overleden) = a manual "chip afmelden" confirmation shown whenever a
  // selected animal has a chip, independent of PetBase config (Jmix releaseHasChipNumber). The
  // register flows (adoption/return/exchange) only show the PetBase step when PetBase is configured.
  const showPetbaseChipStep =
    chippedAnimals.length > 0 && (option === EUTHANASIA || option === DECEASED || releasePetbaseConfigured);
  // Death flows (euthanasia/deceased) deregister the chip (afvoer@10) rather than transferring it; the step
  // is rendered as a per-animal deregistration card (Jmix EuthanasiaChipDeregistrationFragment parity).
  const deregisterMode = option === EUTHANASIA || option === DECEASED;
  // The deregistration card shows the chip read-only (no field to correct), so its only gate is "is there a
  // chip" — already true here. Transfers keep the strict 15-digit capture check on the editable field.
  const chipStepValid =
    deregisterMode || chippedAnimals.every((a) => releaseChipErrors(chipDraft[a.id]).length === 0);

  // Seed a default draft per chipped animal once (chip number from the feed). Transfers default the
  // captured-status checkbox checked (Jmix capture default); death flows default it unchecked so the
  // operator actively confirms each afmelding, matching the Jmix deregistration fragment. Paspoort/
  // kenmerken are seeded later by each transfer card from its /animals/{id} detail (unused in death flows).
  useEffect(() => {
    if (!showPetbaseChipStep) return;
    setChipDraft((cur) => {
      let changed = false;
      const next = { ...cur };
      for (const a of chippedAnimals) {
        if (!next[a.id]) {
          next[a.id] = {
            chipNumber: (a.chip ?? "").trim(),
            chipRegistered: !deregisterMode,
            passportNumber: "",
            kenmerken: "",
          };
          changed = true;
        } else if (next[a.id].chipRegistered === deregisterMode) {
          // wizard-jmix-parity: animals are picked BEFORE the type now, so drafts seed in transfer
          // mode; when the type flips transfer ⇄ death re-apply the mode default (checked for a
          // transfer capture, unchecked for a death afmelding — the chip step comes after the type,
          // and a type change resets its branch anyway).
          next[a.id] = { ...next[a.id], chipRegistered: !deregisterMode };
          changed = true;
        }
      }
      return changed ? next : cur;
    });
  }, [showPetbaseChipStep, chippedAnimals, deregisterMode]);

  function setChip(id: number, patch: Partial<ReleaseChipCapture>) {
    setChipDraft((cur) => ({ ...cur, [id]: { ...cur[id], ...patch } }));
  }

  function seedChipDetail(id: number, patch: Partial<ReleaseChipCapture>) {
    if (detailSeeded.current.has(id)) return;
    detailSeeded.current.add(id);
    setChipDraft((cur) => ({ ...cur, [id]: { ...cur[id], ...patch } }));
  }

  // Key-based step model (dynamic): type-specific steps insert between Dieren and Controle.
  // Collapsed-bar summaries (Jmix StepCard.createCollapsedSummary): "Label: value1 | value2".
  const sum = (label: string, ...values: (string | null | undefined)[]) =>
    `${label}: ${values.map((v) => (v == null || v === "" ? "-" : v)).join(" | ")}`;

  // The animals band: a 56px photo + "chip · naam · soort · ras · geslacht · kleur · vacht" per animal,
  // like the intake wizard. It is the one recap worth keeping — it says WHICH animal you are releasing
  // on every later step. (The count and the destination bands are gone: they only echoed a click.)
  const animalsSummary = (
    <div className="fawkes-stepwiz__animal-rows">
      {selected.map((a) => (
        <div className="fawkes-stepwiz__animal-row" key={a.id}>
          <AnimalThumb fotoUrl={photoOf(a.id, a.fotoUrl)} alt={a.naam} noPhotoLabel={t("inboeken.results.noPhoto")} />
          <span className="fawkes-stepwiz__animal-row-label">
            {animalRowText({
              chip: a.chip, naam: a.naam, soort: a.soort, ras: a.ras,
              geslacht: a.geslacht, kleur: a.kleur, vacht: a.vacht,
            })}
          </span>
        </div>
      ))}
    </div>
  );
  const plannedCount = locked ? 1 : multi ? Number(countInput) || 0 : 1;
  const countInvalid = multi == null || (multi && (plannedCount < 2 || plannedCount > MAX_ANIMAL_COUNT));


  // wizard-jmix-parity: the Jmix step ORDER — animal count + animals BEFORE the release type
  // (ShelterReleaseWizard.resolveStepSequence), with per-step collapsed summaries.
  const stepDefs = [
    // No recap bands for the count and the animals: the chosen animals are printed in the wizard's own
    // header line on every later step, so "Geselecteerd aantal: 1" + "Dieren:" were two empty-looking
    // rows repeating what is already on screen.
    { key: "count", title: t("uitboeken.count.header"), summary: undefined, content: renderCount() },
    { key: "animals", title: t("uitboeken.step.animals"), summary: animalsSummary, content: renderAnimals(), hasBodyTitle: true },
    // No recap band for the type either: the stepper already names the phase and the chosen type drives
    // the steps that follow, so "Vertrek: Uitgezet" was a row that only repeated a click you just made.
    { key: "type", title: t("uitboeken.type.header"), summary: undefined, content: renderType() },
    ...(option === ADOPTION
      ? [
          // Zolang de bekende eigenaar nog bevestigd moet worden heet de stap "Eigenaar controleren"
          // (net als in de inboek-wizard); daarna gaat hij over de nieuwe eigenaar. Eén kop — de body
          // draagt hem niet zelf, dus de modal-intro is de enige.
          { key: "adoptionRelation", title: t("uitboeken.step.newOwner"), summary: sum(t("uitboeken.step.newOwner"), newOwnerLabel), content: renderRelation(), hasBodyTitle: false, hideActions: relationPhase === "find" },
          { key: "adoptionCosts", title: t("uitboeken.step.costs"), summary: sum(t("uitboeken.step.costs"), formatEuro(adoptionCostTotal, locale)), content: renderAdoptionCosts(), hasBodyTitle: true },
          { key: "adoptionTerms", title: t("uitboeken.step.terms"), summary: sum(t("uitboeken.step.terms"), adoptionTermsBlocked ? null : t("wizard.agreed")), content: renderAdoptionTerms(), hasBodyTitle: true },
          { key: "adoptionPayment", title: t("uitboeken.step.payment"), summary: sum(t("uitboeken.step.payment"), formatEuro(adoptionPaidNow, locale)), content: renderAdoptionPayment(), hasBodyTitle: true },
        ]
      : []),
    ...(option === RETURN_TO_OWNER
      ? [
          { key: "returnRelation", title: t("uitboeken.step.owner"), summary: sum(t("uitboeken.step.owner"), newOwnerLabel), content: renderReturnRelation(), hasBodyTitle: !returnAsksOwner, hideActions: !returnAsksOwner && relationPhase === "find" },
          { key: "returnDetails", title: t("uitboeken.step.details"), summary: sum(t("uitboeken.step.details"), formatDate(releaseDate, locale)), content: renderReturnDetails(), hasBodyTitle: true },
          { key: "returnDeclaration", title: t("uitboeken.step.declaration"), summary: sum(t("uitboeken.step.declaration"), returnDeclarationBlocked ? null : t("wizard.agreed")), content: renderReturnDeclaration(), hasBodyTitle: true },
          { key: "returnPayment", title: t("uitboeken.step.payment"), summary: sum(t("uitboeken.step.payment"), formatEuro(returnPaidNow, locale)), content: renderReturnPayment(), hasBodyTitle: true },
        ]
      : []),
    ...(option === EUTHANASIA
      ? [{ key: "euthanasiaDetails", title: t("uitboeken.step.details"), summary: sum(t("uitboeken.step.details"), formatDate(releaseDate, locale), euthanasia.performer), content: renderEuthanasia() }]
      : []),
    ...(option === DECEASED
      ? [{ key: "deceasedDetails", title: t("uitboeken.step.details"), summary: sum(t("uitboeken.step.details"), formatDate(releaseDate, locale), deceased.cause), content: renderDeceased() }]
      : []),
    ...(option === NATURE_RELEASE
      // No recap band: the date and the town are both on the summary step a moment later, and the
      // animal row above already says what this wizard is about.
      ? [{ key: "natureDetails", title: t("uitboeken.step.details"), summary: undefined, content: renderNature() }]
      : []),
    ...(option === EXCHANGE
      ? [
          { key: "exchangeParty", title: t("uitboeken.step.party"), summary: sum(t("uitboeken.step.party"), newOwnerLabel), content: renderExchangeParty(), hasBodyTitle: true },
          { key: "exchangeDetails", title: t("uitboeken.step.details"), summary: sum(t("uitboeken.step.details"), exchangeReason), content: renderExchangeDetails() },
        ]
      : []),
    ...(showPetbaseChipStep
      ? [
          {
            key: "petbaseChip",
            title: deregisterMode ? t("uitboeken.step.chip") : t("uitboeken.step.petbaseChip"),
            summary: sum(deregisterMode ? t("uitboeken.step.chip") : t("uitboeken.step.petbaseChip"), String(chippedAnimals.length)),
            content: renderPetbaseChipStep(),
            hasBodyTitle: true,
          },
        ]
      : []),
    // hasBodyTitle: the per-flow summaries carry their own Jmix header ("Adoptieoverzicht" /
    // "Samenvatting" + info line) — the card-level h3 would double it (audit §1 row 5).
    { key: "summary", title: t("uitboeken.step.summary"), summary: t("uitboeken.step.summary"), content: renderSummary(), hasBodyTitle: true },
  ];
  const currentKey = stepDefs[Math.min(step, stepDefs.length - 1)]?.key;

  // Server-rendered adoption-terms preview (M8): fetch on entering the step and (debounced, like the
  // Jmix signature-refresh @ClientCallable) whenever the live signature or embedded values change.
  useEffect(() => {
    if (currentKey !== "adoptionTerms") return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      fetchWizardDocumentPreview("PLAATSINGSOVEREENKOMST", buildValues())
        .then((pages) => {
          if (!cancelled) setTermsPreviewPages(pages);
        })
        .catch(() => {
          if (!cancelled) setTermsPreviewPages([]);
        });
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // buildValues reads the whole draft; re-render only on the inputs the template embeds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey, adoptionTerms.signature, adoptionTerms.signedLater, adoptionCosts, newOwnerId, releaseDate]);

  const proceedDisabled =
    (currentKey === "count" && countInvalid) ||
    (currentKey === "type" && !soortId) ||
    (currentKey === "animals" && (selected.length === 0 || (!locked && selected.length !== plannedCount))) ||
    (currentKey === "adoptionRelation" && (newOwnerId == null || blokkadeBlocked || !releaseDate)) ||
    (currentKey === "returnRelation" && (newOwnerId == null || blokkadeBlocked || !releaseDate)) ||
    // Jmix requires at least one registered payment before leaving the Betaling step when anything is
    // due (the adoption fee is always ≥ €0; a €0 due lets the step pass with nothing registered).
    (currentKey === "adoptionPayment" && adoptionCostTotal > 0 && adoptionPaidTotal <= 0) ||
    (currentKey === "returnPayment" && returnCostTotal > 0 && returnPaidTotal <= 0) ||
    (currentKey === "adoptionPayment" && adoptionTeveel) ||
    (currentKey === "returnPayment" && returnTeveel) ||
    (currentKey === "adoptionCosts" && (adoptionBtwOntbreekt || adoptionKortingTeHoog)) ||
    (currentKey === "returnDetails" && (returnBtwOntbreekt || returnKortingTeHoog)) ||
    (currentKey === "adoptionTerms" && adoptionTermsBlocked) ||
    (currentKey === "returnDeclaration" && returnDeclarationBlocked) ||
    (currentKey === "euthanasiaDetails" && euthanasiaBlocked) ||
    (currentKey === "deceasedDetails" && deceasedBlocked) ||
    (currentKey === "natureDetails" && natureBlocked) ||
    (currentKey === "exchangeParty" && newOwnerId == null) ||
    (currentKey === "exchangeDetails" && exchangeBlocked) ||
    (currentKey === "petbaseChip" && !chipStepValid);

  // wizard-jmix-parity navigation (Jmix planAdvance): Bevestigen on the frontier appends the next
  // card; on an edited earlier step it resumes to the frontier (FOCUS_RESUME) — except the TYPE
  // step, which always clears downstream progress (branch reset), and an edited count that no
  // longer matches the selection, which lands on the Dieren step to fix it.
  // Doorstappen na het aanmaken van een relatie. Niet meteen in de callback: de gekozen relatie
  // staat dan nog niet in de state waar de volgende stap en het bewaren van het concept op leunen.
  useEffect(() => {
    if (!naAanmakenDoor || newOwnerId == null) return;
    setNaAanmakenDoor(false);
    confirmStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [naAanmakenDoor, newOwnerId]);

  const typeIndex = stepDefs.findIndex((s) => s.key === "type");
  const confirmStep = () => {
    if (step >= stepDefs.length - 1) {
      submit();
      return;
    }
    // Verlaat je een betaalstap terwijl er een bedrag blijft openstaan, dan eerst de vraag of dat klopt.
    const oordeel = currentKey === "adoptionPayment" ? adoptionOordeel
      : currentKey === "returnPayment" ? returnOordeel : null;
    if (oordeel?.restant && restantVraag == null) {
      setRestantBedrag(oordeel.openstaand);
      setRestantVraag(() => () => doeConfirmStep());
      return;
    }
    doeConfirmStep();
  };

  const doeConfirmStep = () => {
    // De eigenaarstap verlaten bewaart wat er in zijn detailformulier is bijgewerkt (geen aparte
    // opslaan-knop — Bevestigen bewaart, net als in de inboek-wizard).
    if (currentKey === "adoptionRelation" || currentKey === "returnRelation") {
      relationSaveRef.current?.();
    }
    persistDraft();
    if (currentKey === "type") {
      setFurthest(typeIndex + 1);
      setStep(typeIndex + 1);
    } else if (currentKey === "count" && step < furthest && !locked && selected.length !== plannedCount) {
      setStep(1);
    } else if (step < furthest) {
      setStep(furthest);
    } else {
      setFurthest(step + 1);
      setStep(step + 1);
    }
  };
  const editStep = (i: number) => {
    persistDraft();
    setStep(i);
  };
  // Functiedeclaratie, geen const: het zoekscherm in de eigenaarstap krijgt dit als onBack mee, en dat
  // scherm wordt al opgebouwd terwijl stepDefs wordt samengesteld — hierboven dus.
  function backStep() {
    persistDraft();
    setStep((s) => Math.max(0, s - 1));
  }

  // Het doorschuiven na "Selecteer" (zie kiesDier) gebeurt hier: de selectie staat inmiddels in de
  // state, dus de stap-gate oordeelt over de nieuwe selectie en de conceptwizard krijgt het dier mee.
  useEffect(() => {
    if (!doorschuiven) return;
    setDoorschuiven(false);
    if (currentKey === "animals" && !proceedDisabled) confirmStep();
    // confirmStep sluit over de state van deze render — precies de bijgewerkte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doorschuiven, currentKey, proceedDisabled]);


  return (
    <div className="fawkes-inboeken">
      {notice && (
        <div className={`fawkes-inboeken__notice fawkes-inboeken__notice--${notice}`} role="alert">
          {t(`uitboeken.notice.${notice}`)}
        </div>
      )}
      <WizardModal
        title={t("uitboeken.title")}
        phases={UITBOEKEN_PHASES}
        steps={stepDefs.map((s) => ({
          ...s,
          // No text recap bands, anywhere: "Gegevens: 16-08-2026 | Fam. J. Fikkert", "Kosten: € 250,00"
          // and friends only echoed a click you just made, one grey row per completed step. The ANIMAL
          // rows are the exception (a real node, not a string) — they say which animal this is about.
          summary: typeof s.summary === "string" ? undefined : s.summary,
          phase: uitboekenPhaseOf(s.key),
          subtitle: s.key === "count" ? t("wizard.count.sub") : undefined,
        }))}
        active={Math.min(step, stepDefs.length - 1)}
        furthest={Math.min(furthest, stepDefs.length - 1)}
        onEdit={editStep}
        onBack={backStep}
        onConfirm={confirmStep}
        onCancel={onCancel}
        onExpand={onExpand}
        expandLabel={t("wizard.openAsPage")}
        confirmDisabled={proceedDisabled}
        busy={busy}
        confirmLabel={t("wizard.confirm")}
        finishLabel={t("uitboeken.finish")}
        backLabel={t("common.back")}
        cancelLabel={t("common.cancel")}
      />
      {/* Een bedrag laten openstaan mag -- een deel nu en de rest later is een gewone afspraak --
          maar niet per ongeluk. Daarom geen blokkade maar een vraag, met het bedrag erin zodat je
          ziet waar je ja tegen zegt (Maarten, 2026-10-24). */}
      <Modal
        open={restantVraag != null}
        title={t("betaling.restantTitel")}
        onClose={() => setRestantVraag(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRestantVraag(null)}>
              {t("betaling.restantNee")}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                const verder = restantVraag;
                setRestantVraag(null);
                verder?.();
              }}
            >
              {t("betaling.restantJa")}
            </Button>
          </>
        }
      >
        <p>{t("betaling.restantVraag", { args: [formatEuro(Math.abs(restantBedrag), locale)] })}</p>
      </Modal>
      <Modal
        open={pendingTypeId != null}
        title={t("uitboeken.typeChange.title")}
        onClose={() => setPendingTypeId(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingTypeId(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (pendingTypeId != null) applyType(pendingTypeId);
                setPendingTypeId(null);
              }}
            >
              {t("uitboeken.typeChange.confirm")}
            </Button>
          </>
        }
      >
        <p>{t("uitboeken.typeChange.body")}</p>
      </Modal>
      <ProductTariefPicker
        open={productPickerOpen}
        onClose={() => setProductPickerOpen(false)}
        onPick={addAdoptionCostFromPicker}
        t={t}
        locale={locale}
      />
    </div>
  );

  // ── step bodies ────────────────────────────────────────────────────────────────────────────────

  // The animals-bar "Toevoegen" (Jmix add-animals summary): bump the planned count and reopen the
  // Dieren step. Function declaration — referenced by animalsSummary above (hoisted).
  function addMoreAnimals() {
    setMulti(true);
    setCountInput(String(Math.min(MAX_ANIMAL_COUNT, Math.max(2, selected.length + 1))));
    persistDraft();
    setStep(1);
  }

  function renderCount() {
    // Jmix ShelterReleaseSelectAnimalCountFragment: Één dier / Meerdere dieren as 20em illustrated
    // cards (green-filtered paw/kittens SVGs), the count field INSIDE the Meerdere card, GREEN
    // selected accent. The Meerdere card is a div[role=button] so the nested input stays valid markup.
    return (
      <div className="fawkes-inboeken__step">
        <div className="fawkes-om-modal__cards">
          <button
            type="button"
            aria-pressed={multi === false}
            className={"fawkes-om-modal__card" + (multi === false ? " fawkes-om-modal__card--selected" : "")}
            onClick={() => setMulti(false)}
          >
            <span className="fawkes-om-modal__radio">
              {multi === false && <span className="fawkes-om-modal__radio-dot" />}
            </span>
            <span className="fawkes-om-modal__card-media">
              <PawIllustration />
            </span>
            <span className="fawkes-om-modal__card-title">{t("wizard.count.single")}</span>
            <span className="fawkes-om-modal__card-desc">{t("uitboeken.count.singleDesc")}</span>
          </button>
          <div
            role="button"
            tabIndex={0}
            aria-pressed={multi === true}
            className={"fawkes-om-modal__card" + (multi === true ? " fawkes-om-modal__card--selected" : "")}
            onClick={() => setMulti(true)}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
                e.preventDefault();
                setMulti(true);
              }
            }}
          >
            <span className="fawkes-om-modal__radio">
              {multi === true && <span className="fawkes-om-modal__radio-dot" />}
            </span>
            <span className="fawkes-om-modal__card-media">
              <KittensIllustration />
            </span>
            <span className="fawkes-om-modal__card-title">{t("wizard.count.multiple")}</span>
            <span className="fawkes-om-modal__card-desc">{t("uitboeken.count.multiDesc")}</span>
            {/* Count field always shown so the operator can click straight into it; focusing or typing
                also picks the "Meerdere dieren" card. */}
            <label className="fawkes-om-modal__count-field" onClick={(e) => e.stopPropagation()}>
              <span className="fawkes-om-modal__count-label">{t("wizard.count.amount")}</span>
              <input
                className="fawkes-om-modal__count-input"
                type="number"
                aria-label={t("wizard.count.amount")}
                min={2}
                max={MAX_ANIMAL_COUNT}
                value={countInput}
                onFocus={() => setMulti(true)}
                onChange={(e) => {
                  setMulti(true);
                  setCountInput(e.target.value);
                }}
              />
              <span className="fawkes-inboeken__helper">{t("uitboeken.count.helper")}</span>
            </label>
          </div>
        </div>
      </div>
    );
  }

  function applyType(id: string) {
    setSoortId(id);
    resetDetails();
    // Jmix branch reset: switching the type removes the already-built downstream steps; they are
    // re-appended (for the new branch) when the operator confirms the type step again.
    setFurthest((f) => Math.min(f, typeIndex));
    setStep(typeIndex);
  }

  function renderType() {
    // Fixed reading order (TYPE_ORDER); anything the list doesn't name keeps its feed position at the
    // end, before the unavailable cards.
    const rang = (s: VertrekSoort) => {
      const i = TYPE_ORDER.indexOf(typeKey(s));
      return i === -1 ? TYPE_ORDER.length : i;
    };
    const kaarten = [...soorten].sort((a, b) => rang(a) - rang(b));
    return (
      <div className="fawkes-inboeken__step">
        <div className="fawkes-om-modal__cards fawkes-om-modal__cards--compact">
          {kaarten.map((s) => {
            const key = typeKey(s);
            const descKey = TYPE_DESC_KEY[key];
            const active = String(s.id) === soortId;
            return (
              <button
                type="button"
                key={s.id}
                disabled={s.alwaysDisabled}
                aria-pressed={active}
                title={s.alwaysDisabled ? t("uitboeken.soort.unavailable") : undefined}
                className={
                  "fawkes-om-modal__card" +
                  (active ? " fawkes-om-modal__card--selected" : "") +
                  (s.alwaysDisabled ? " fawkes-om-modal__card--disabled" : "")
                }
                onClick={() => {
                  if (s.alwaysDisabled || active) return;
                  // Jmix ShelterReleaseDetailView: changing an already-chosen vertreksoort while
                  // later steps exist wipes those steps — ask first ("Vertreksoort wijzigen?").
                  if (soortId && furthest > typeIndex) {
                    setPendingTypeId(String(s.id));
                    return;
                  }
                  applyType(String(s.id));
                }}
              >
                <span className="fawkes-om-modal__radio">
                  {active && <span className="fawkes-om-modal__radio-dot" />}
                </span>
                <span className="fawkes-om-modal__card-media">
                  <img className="fawkes-inboeken__type-icon" src={TYPE_ICON[key] ?? TYPE_ICON.other} alt="" />
                </span>
                <span className="fawkes-om-modal__card-title">{s.label}</span>
                {descKey && <span className="fawkes-om-modal__card-desc">{t(descKey)}</span>}
                {s.alwaysDisabled && (
                  <span className="fawkes-inboeken__type-unavailable">{t("inboeken.soort.unavailableShort")}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // "Selecteer" betekent aan de balie: dít is hem. Zodra het laatste geplande dier gekozen is
  // schuift de wizard zelf door naar de volgende stap, in plaats van je eerst langs de hele
  // dierenlijst naar Bevestigen te sturen. Bij "Meerdere dieren" gebeurt dat pas als het afgesproken
  // aantal er staat; nogmaals klikken op een gekozen dier haalt het er gewoon weer af.
  //
  // Het doorschuiven wacht één render (setDoorschuiven → effect), want confirmStep() bewaart de
  // conceptwizard met buildValues(), en dat leest de state van déze render — het net gekozen dier
  // zou er dan nog niet in staan.
  function kiesDier(a: PresentAnimalCard, alGekozen: boolean) {
    if (alGekozen) {
      setSelected((cur) => cur.filter((x) => x.id !== a.id));
      return;
    }
    setSelected((cur) => [...cur, toSelected(a)]);
    setDoorschuiven(true);
  }

  function renderAnimals() {
    // Jmix release picker = the vestiging-scoped "Aanwezige dieren" photo-card grid with live
    // Naam/Chipnummer/Diersoort/Ras filters (AnimalSearchResultsFragment releasePresentOverviewMode).
    // Only present animals appear, so being on a card IS the eligibility (inverted vs intake).
    return (
      <div className="fawkes-inboeken__step">
        <StepIntro title={t("uitboeken.animals.presentHeader")} info={t("uitboeken.animals.info")} />
        <ul className="fawkes-inboeken__selected">
          {selected.map((a) => (
            <li key={a.id} className="fawkes-animalsub__selected-row">
              <AnimalThumb fotoUrl={photoOf(a.id, a.fotoUrl)} alt={a.naam} noPhotoLabel={t("inboeken.results.noPhoto")} />
              <span className="fawkes-stepwiz__animal-row-label">
                {animalRowText({ chip: a.chip, naam: a.naam, soort: a.soort, ras: a.ras, geslacht: a.geslacht, kleur: a.kleur, vacht: a.vacht })}
              </span>
              {!locked && (
                <button
                  type="button"
                  className="fawkes-inboeken__link"
                  onClick={() => setSelected((cur) => cur.filter((x) => x.id !== a.id))}
                >
                  {t("inboeken.animals.remove")}
                </button>
              )}
            </li>
          ))}
          {selected.length === 0 && (
            <li className="fawkes-inboeken__muted">{t("inboeken.animals.none")}</li>
          )}
        </ul>
        {/* "Nog een dier erbij" used to hang off the recap band above the steps. That band is gone, so
            the affordance lives where the animals are picked: it raises the planned count by one, which
            is what the step's own gate checks. */}
        {!locked && selected.length > 0 && (
          <button type="button" className="fawkes-stepwiz__add" onClick={addMoreAnimals}>
            {t("wizard.animals.add")}
          </button>
        )}
        {!locked && (
          <>
            <div className="fawkes-animalsub__grid fawkes-animalsub__grid--filters">
              <Field label={t("inboeken.search.naam")} value={animalFilters.naam}
                placeholder={t("inboeken.search.naam")}
                onChange={(e) => setAnimalFilters((f) => ({ ...f, naam: e.target.value }))} />
              <Field label={t("inboeken.newAnimal.chip")} value={animalFilters.chip}
                placeholder={t("inboeken.newAnimal.chip.placeholder")}
                onChange={(e) => setAnimalFilters((f) => ({ ...f, chip: e.target.value }))} />
              <ComboBox label={t("inboeken.newAnimal.diersoort")} value={animalFilters.diersoort}
                onChange={(v) => setAnimalFilters((f) => ({ ...f, diersoort: v }))}
                placeholder={t("inboeken.newAnimal.diersoort.placeholder")}
                options={presentSoorten.map((s) => ({ value: s, label: s }))} />
              <ComboBox label={t("inboeken.newAnimal.ras")} value={animalFilters.ras}
                onChange={(v) => setAnimalFilters((f) => ({ ...f, ras: v }))}
                placeholder={t("inboeken.newAnimal.ras.placeholder")}
                options={presentRassen.map((r) => ({ value: r, label: r }))} />
            </div>
            {presentTruncated && (
              <span className="fawkes-inboeken__field-error" role="status">
                {`Let op: alleen de eerste ${presentAnimals.length} van ${presentData?.total} aanwezige dieren zijn geladen.`}
              </span>
            )}
            <ul className="fawkes-animalsub__cards">
              {filteredPresent.map((a) => {
                const isSelected = selected.some((s) => s.id === a.id);
                return (
                  <li key={a.id} className={"fawkes-animalsub__card" + (isSelected ? " is-selected" : "")}>
                    <AnimalThumb fotoUrl={photoOf(a.id, a.fotoUrl)} alt={a.naam ?? ""} noPhotoLabel={t("inboeken.results.noPhoto")} />
                    <div className="fawkes-animalsub__card-facts">
                      <strong>{a.naam ?? `#${a.id}`}</strong>
                      <span>{[a.chipPasnummer, a.diersoort, a.ras].filter(Boolean).join(" · ") || "—"}</span>
                      <span>{[a.geslacht, a.kleur, a.vacht].filter(Boolean).join(" · ") || "—"}</span>
                    </div>
                    <Button
                      variant="secondary"
                      aria-pressed={isSelected}
                      onClick={() => kiesDier(a, isSelected)}
                    >
                      {isSelected ? t("inboeken.animals.remove") : t("inboeken.results.select")}
                    </Button>
                  </li>
                );
              })}
              {filteredPresent.length === 0 && (
                <li className="fawkes-inboeken__muted">{t("inboeken.results.none")}</li>
              )}
            </ul>
          </>
        )}
      </div>
    );
  }

  // The zwarte-lijst banner + overrule reason — shared by the adoption and return-to-owner owner
  // steps (the Jmix BlokkadeAdoptieGuard covers both paths). Null while no active block.
  function renderBlokkadeBanner() {
    if (actiefBlokkade == null) return null;
    return (
      <div className="fawkes-blokkade-banner" role="alert">
        <div className="fawkes-blokkade-banner__header">
          <span className="fawkes-blokkade-banner__icon" aria-hidden="true">⊘</span>
          <span className="fawkes-blokkade-banner__title">{t("uitboeken.blokkade.title")}</span>
        </div>
        <span className="fawkes-blokkade-banner__subtitle">
          {t("uitboeken.blokkade.subtitle")}
          {actiefBlokkade.redenLabel ? ` · ${actiefBlokkade.redenLabel}` : ""}
        </span>
        {canOverrule ? (
          <FieldShell label={t("uitboeken.blokkade.overrideReden")} required>
            <textarea
              className="fui-field__input fawkes-donateur-form__area"
              maxLength={2000}
              value={overrideReden}
              onChange={(e) => setOverrideReden(e.target.value)}
              placeholder={t("uitboeken.blokkade.overridePlaceholder")}
            />
          </FieldShell>
        ) : (
          <span className="fawkes-blokkade-banner__subtitle">{t("uitboeken.blokkade.noOverrule")}</span>
        )}
      </div>
    );
  }

  function renderRelation() {
    // Een plaatsing gaat altijd naar een nieuwe eigenaar, dus hier staat meteen het volledige
    // zoekscherm: e-mail, en/of adres, en de resultaatkaarten met "Nieuwe relatie aanmaken".
    // Hetzelfde scherm als in de inboek-wizard, niet een ingeklapt regeltje in een halve kolom.
    return (
      <div className="fawkes-inboeken__step">
        <RelationPicker
          variant="step"
          label={t(ownerLabelKey)}
          stepTitle={t("inboeken.relation.findTitle")}
          stepInfo={t("uitboeken.relation.findInfo.adoption")}
          valueId={newOwnerId}
          valueLabel={newOwnerLabel}
          canCreate={canCreateRelatie}
          feedPath="release/reference/klanten"
          onBack={backStep}
          onPhaseChange={setRelationPhase}
          registerSave={registerRelationSave}
          onCreated={() => setNaAanmakenDoor(true)}
          onChange={(id, label) => {
            setNewOwnerId(id);
            setNewOwnerLabel(label);
            setOverrideReden("");
          }}
          t={t}
        />
        {renderBlokkadeBanner()}
      </div>
    );
  }


  // A picked catalog line: aantal × prijs collapses into one line amount (Jmix openProductPicker), and
  // the placeholder default is dropped once a real line exists (Jmix verwijderDefaultAlsEnige).
  function addAdoptionCostFromPicker(regel: GekozenProductRegel) {
    setAdoptionCosts((cur) => [
      ...cur.filter((c) => !isDefaultAdoptionLine(c)),
      {
        id: costSeq,
        description: regel.omschrijving,
        amount: (regel.aantal * regel.prijsInclBtw).toFixed(2),
        btw: regel.btwPercentage,
      },
    ]);
    setCostSeq((n) => n + 1);
  }

  function renderAdoptionCosts() {
    return (
      <div className="fawkes-inboeken__step">
        <StepIntro title={t("uitboeken.costs.header")} info={t("uitboeken.costs.info")} />
        <div className="fawkes-inboeken__tarief-propose">
          <button
            type="button"
            className="fawkes-inboeken__link"
            onClick={proposeAdoptionTariff}
            disabled={tariefBusy}
          >
            ✨ {t("inboeken.costs.propose")}
          </button>
          {tariefNotice && (
            <span
              className={
                tariefNotice.type === "ok"
                  ? "fawkes-inboeken__helper"
                  : "fawkes-inboeken__muted"
              }
              role="status"
            >
              {tariefNotice.text}
            </span>
          )}
        </div>
        <ul className="fawkes-inboeken__cost-lines">
          {adoptionCosts.map((c) => (
            <li key={c.id} className="fawkes-inboeken__cost-line">
              <input
                className="fui-field__input"
                value={c.description}
                placeholder={t("inboeken.costs.product")}
                onChange={(e) =>
                  setAdoptionCosts((cur) => cur.map((x) => (x.id === c.id ? { ...x, description: e.target.value } : x)))
                }
              />
              <input
                className="fui-field__input fawkes-inboeken__cost-amount"
                type="text"
                inputMode="decimal"
                value={c.amount}
                placeholder={t("inboeken.costs.amount.placeholder")}
                onChange={(e) =>
                  setAdoptionCosts((cur) => cur.map((x) => (x.id === c.id ? { ...x, amount: e.target.value } : x)))
                }
              />
              {/* Korting per regel. Dat is waar hij hoort: de btw van de korting is dan vanzelf die
                  van de regel waar hij op zit, en je kunt op het ene product wel korting geven en op
                  het andere niet (Maarten, 2026-10-24). */}
              <input
                className="fui-field__input fawkes-inboeken__cost-korting"
                type="text"
                inputMode="decimal"
                value={c.korting ?? ""}
                placeholder={t("korting.placeholder")}
                aria-label={t("korting.label")}
                title={t("korting.label")}
                onChange={(e) =>
                  setAdoptionCosts((cur) => cur.map((x) => (x.id === c.id ? { ...x, korting: e.target.value } : x)))
                }
              />
              <KostenregelBtw
                value={c.btw}
                onChange={(btw) => setAdoptionCosts((cur) => cur.map((x) => (x.id === c.id ? { ...x, btw } : x)))}
                percentages={btwPercentages}
                missing={parseAmount(c.amount) != null && c.btw == null}
                t={t}
              />
              {/* The default base-fee line cannot be removed (Jmix hides its trash icon); deleting the
                  last real line re-seeds the default, like the Jmix state service. */}
              {!isDefaultAdoptionLine(c) && (
                <button
                  type="button"
                  className="fawkes-inboeken__link"
                  onClick={() =>
                    setAdoptionCosts((cur) => {
                      const next = cur.filter((x) => x.id !== c.id);
                      return next.length === 0
                        ? [{ id: 0, description: ADOPTION_DEFAULT_PRODUCT, amount: ADOPTION_DEFAULT_AMOUNT.toFixed(2) }]
                        : next;
                    })
                  }
                >
                  {t("inboeken.animals.remove")}
                </button>
              )}
            </li>
          ))}
          {adoptionCosts.length === 0 && (
            <li className="fawkes-inboeken__muted">{t("inboeken.costs.empty")}</li>
          )}
        </ul>
        {adoptionBtwOntbreekt && (
          <div className="fawkes-inboeken__field-error" role="alert">{t("btw.verplicht")}</div>
        )}
        <button
          type="button"
          className="fawkes-inboeken__link"
          onClick={() => setProductPickerOpen(true)}
        >
          {t("inboeken.costs.add")}
        </button>
        <div className="fawkes-inboeken__cost-total fawkes-inboeken__cost-total--grand">
          <span className="fui-field__label">{t("inboeken.costs.total")}</span>
          <strong>{formatEuro(adoptionCostTotal, locale)}</strong>
        </div>
      </div>
    );
  }

  function doPrintAdoptionVerklaring() {
    // Real uitboek-afdruk.pdf — the exact document the archiver stores on completion. Falls back to
    // the legacy client-side print when the server render fails, so the button never dead-ends.
    downloadWizardDocumentPdf("PLAATSINGSOVEREENKOMST", buildValues()).catch(() => legacyPrintAdoptionVerklaring());
  }

  function legacyPrintAdoptionVerklaring() {
    printVerklaring({
      title: t("uitboeken.adoptionTerms.declTitle"),
      introText: t("uitboeken.adoptionTerms.declIntro"),
      declarationText: t("uitboeken.adoptionTerms.declText"),
      contactTitle: t("uitboeken.field.newOwner"),
      contactName: newOwnerLabel ?? "",
      contactAddress: "",
      contactPhone: "",
      contactEmail: "",
      animalsLabel: t("uitboeken.step.animals"),
      animals: selected.map((a) => a.label).join(", "),
      signatureLabel: t("inboeken.terms.signature"),
      signatureDataUrl: adoptionTerms.signature || null,
      dateLabel: t("inboeken.terms.date"),
      date: adoptionTerms.date,
      placeLabel: t("inboeken.terms.place"),
      place: adoptionTerms.place,
      costs: { label: t("inboeken.costs.total"), total: formatEuro(adoptionCostTotal, locale) },
    });
  }

  function renderAdoptionTerms() {
    return (
      <div className="fawkes-inboeken__step">
        <StepIntro title={t("uitboeken.adoptionTerms.header")} info={t("uitboeken.adoptionTerms.info")} />
        <RelationContactPanel
          klantId={newOwnerId}
          label={t("uitboeken.field.newOwner")}
          t={t}
          urlBase="release/reference/klanten"
        />
        {termsPreviewPages.length > 0 ? (
          /* M8: the real plaatsingsovereenkomst as page images — exactly the archived document,
             with the live signature embedded server-side (Jmix AdoptionTermsFragment). */
          <div className="fawkes-inboeken__terms-preview">
            {termsPreviewPages.map((src, i) => (
              <img
                key={i}
                className="fawkes-inboeken__terms-preview-page"
                src={src}
                alt={`${t("uitboeken.adoptionTerms.declTitle")} ${i + 1}`}
              />
            ))}
          </div>
        ) : (
          /* Fallback when the server preview is unavailable, mirroring the Jmix fragment's fallback. */
          <div className="fawkes-inboeken__terms-declaration">
            <strong>{t("uitboeken.adoptionTerms.declTitle")}</strong>
            <p>{t("uitboeken.adoptionTerms.declIntro")}</p>
            <p className="fawkes-inboeken__terms-text">{t("uitboeken.adoptionTerms.declText")}</p>
            <div className="fawkes-inboeken__cost-total">
              <span className="fui-field__label">{t("inboeken.costs.total")}</span>
              <strong>{formatEuro(adoptionCostTotal, locale)}</strong>
            </div>
          </div>
        )}
        {/* Akkoord links, "tekent later" als knop rechts — zoals bij afstand. Twee vinkjes onder
            elkaar lazen als twee voorwaarden waaraan je allebei moet voldoen, terwijl dit juist een
            uitweg is: je gaat verder zónder handtekening. De plaatsingsovereenkomst wordt dan nog
            steeds bij het verblijf bewaard. */}
        <div className="fawkes-terms__agree-row">
          <label className="fawkes-terms__toggle">
            <input
              type="checkbox"
              role="switch"
              checked={adoptionTerms.accepted}
              onChange={(e) => setAdoptionTerms((s) => ({ ...s, accepted: e.target.checked }))}
            />
            <span>{t("uitboeken.adoptionTerms.agree")}</span>
          </label>
          <Button
            variant={adoptionTerms.signedLater ? "primary" : "secondary"}
            onClick={() => setAdoptionTerms((s) => ({ ...s, signedLater: !s.signedLater }))}
          >
            {t("uitboeken.adoptionTerms.signLater")}
          </Button>
        </div>
        {adoptionTermsBlocked && (
          <span className="fawkes-inboeken__field-error">
            {!adoptionTerms.accepted ? t("uitboeken.adoptionTerms.agreeRequired") : t("inboeken.terms.signatureRequired")}
          </span>
        )}
        <div className="fawkes-terms__sign-row">
          {/* Het label staat in een eigen rasterrij, zodat de handtekeningkaart en de feiten
              ernaast op dezelfde regel beginnen en eindigen. */}
          <span className="fui-field__label fawkes-terms__sign-label">
            {t("inboeken.terms.signature")}
          </span>
          <div className="fawkes-terms__sign">
            {adoptionTerms.signedLater ? (
              <p className="fawkes-inboeken__muted">{t("uitboeken.adoptionTerms.signLater")}</p>
            ) : (
              <SignaturePad
                value={adoptionTerms.signature}
                onChange={(d) => setAdoptionTerms((s) => ({ ...s, signature: d }))}
                retryLabel={t("inboeken.terms.signatureRetry")}
                placeholder={t("inboeken.terms.signaturePlaceholder")}
              />
            )}
          </div>
          {/* Zoals bij de afstandsverklaring: datum en plaats zijn feiten naast de handtekening,
              geen invulvelden. De datum is de uitboekdatum, de plaats de vestiging waar getekend
              wordt. Het bedrag sluit het blok af. */}
          <aside className="fawkes-terms__meta">
            <p className="fawkes-terms__meta-line">
              <strong>{t("inboeken.terms.date")}:</strong>{" "}
              {adoptionTerms.date ? formatDate(adoptionTerms.date, locale) : "—"}
            </p>
            <p className="fawkes-terms__meta-line">
              <strong>{t("inboeken.terms.place")}:</strong> {adoptionTerms.place || "—"}
            </p>
            <div className="fawkes-terms__meta-total">
              <span>{t("inboeken.costs.total")}</span>
              <strong>{formatEuro(adoptionCostTotal, locale)}</strong>
            </div>
          </aside>
        </div>
        {/* Een knop met printericoon, zoals bij afstand — als kale tekstregel las hij als een
            voetnoot terwijl het de enige actie op dit blok is. */}
        <div className="fawkes-terms__print">
          <Button variant="secondary" onClick={doPrintAdoptionVerklaring}>
            <Printer size={16} aria-hidden="true" />
            {t("inboeken.terms.print")}
          </Button>
        </div>
      </div>
    );
  }

  /**
   * Het betaalscherm van afstand, hergebruikt voor ADOPTION en RETURN_TO_OWNER (Jmix deelt hier ook
   * één AdoptionPaymentFragment): kies een methode, vul de bedragen, met het lopende
   * betalingsoverzicht ernaast. Alleen de introregel benoemt om welke kosten het gaat.
   */
  function renderPaymentMethods(
    payment: { pin: string; cash: string; later: string; reference: string },
    setPayment: (updater: (s: { pin: string; cash: string; later: string; reference: string }) => { pin: string; cash: string; later: string; reference: string }) => void,
    method: ReleasePayTile | null,
    setMethod: (key: ReleasePayTile) => void,
    due: number,
    headerKey: string,
    infoKey: string,
  ) {
    // "Voldaan" is wat nu binnenkomt; het factuurbedrag telt daar niet in mee maar haalt het bedrag
    // wel van het openstaande af — daar komt immers een factuur voor.
    const paidNow = (parseAmount(payment.pin) ?? 0) + (parseAmount(payment.cash) ?? 0);
    const later = parseAmount(payment.later) ?? 0;
    const remaining = due - paidNow - later;
    const oordeel = betalingOordeel(due, paidNow + later);
    const full = formatPayAmount(due);
    return (
      <PaymentStep
        title={t(headerKey)}
        info={t(infoKey)}
        methods={RELEASE_PAY_METHODS.map((m) => ({ ...m, label: t(m.labelKey) }))}
        selectedMethod={method}
        onPickMethod={(key) => {
          setMethod(key as ReleasePayTile);
          setPayment((p) => ({
            ...p,
            pin: key === "pin" ? full : "",
            cash: key === "cash" ? full : "",
            later: key === "invoice" ? full : "",
          }));
        }}
        rows={RELEASE_PAY_ROWS.map((r) => ({ ...r, label: t(r.labelKey) }))}
        values={payment}
        amountPlaceholder={t("inboeken.costs.amount.placeholder")}
        onAmountChange={(key, value) => setPayment((p) => ({ ...p, [key]: value }))}
        remark={{
          label: t("inboeken.payment.invoiceRemark"),
          placeholder: t("inboeken.payment.invoiceRemark.placeholder"),
          value: payment.reference,
          onChange: (value) => setPayment((p) => ({ ...p, reference: value })),
        }}
        due={{ label: t("inboeken.payment.due"), amount: formatEuro(due, locale) }}
        paid={{ label: t("inboeken.payment.paid"), amount: formatEuro(paidNow, locale) }}
        open={{ label: t("inboeken.payment.open"), amount: formatEuro(remaining, locale) }}
        remaining={{ label: t("inboeken.payment.remaining"), amount: formatEuro(remaining, locale) }}
        note={later > 0 ? t("inboeken.payment.invoiceNote") : undefined}
        error={
          due > 0 && paidNow + later > due
            ? t(oordeel.fout === "TEGENGESTELD" ? "betaling.tegengesteld" : "uitboeken.payment.teveel",
                { args: [formatEuro(Math.abs(paidNow + later - due), locale)] })
            : due > 0 && paidNow + later <= 0
              ? t("uitboeken.payment.methodRequired")
              : undefined
        }
      />
    );
  }
  function renderAdoptionPayment() {
    return renderPaymentMethods(
      adoptionPayment,
      setAdoptionPayment,
      adoptionPayMethod,
      setAdoptionPayMethod,
      adoptionCostTotal,
      "uitboeken.payment.header",
      "uitboeken.payment.info",
    );
  }

  // Owner step — pre-suggests the original owner from the active stay's intake relation (klantIn) before
  // falling back to manual search, mirroring the Jmix ReturnToOwnerRelationFragment Yes/No flow.
  function renderReturnRelation() {
    // Dezelfde vraag als bij een plaatsing: de bekende eigenaar op een kaart met Ja/Nee, en bij "Nee"
    // het volledige zoekscherm. De vertrekdatum stond hier in een halve kolom ernaast; die is eruit —
    // een uitboeking krijgt de datum van vandaag, net als een plaatsing.
    if (returnAsksOwner) {
      return (
        <div className="fawkes-inboeken__step">
          <OwnerConfirmCard
            owner={returnKnownOwner!}
            choice={returnOwnerAnswer}
            question={t("uitboeken.return.knownOwner.question")}
            yesLabel={t("uitboeken.return.knownOwner.yes")}
            noLabel={t("uitboeken.return.knownOwner.no")}
            onYes={() => {
              setReturnOwnerAnswer("yes");
              setNewOwnerId(returnKnownOwner!.ownerId);
              setNewOwnerLabel(returnKnownOwner!.ownerLabel);
              setReturnOwnerSource(ORIGINAL_OWNER_SOURCE_INTAKE);
              setOverrideReden("");
            }}
            onNo={() => {
              setReturnOwnerAnswer("no");
              setNewOwnerId(null);
              setNewOwnerLabel(null);
              setReturnOwnerSource(null);
              setOverrideReden("");
            }}
            t={t}
          />
          {/* Jmix guards BOTH return paths — the pre-suggestion "Ja" pin and a manual pick — so the
              banner sits buiten de tak (ReturnToOwnerRelationFragment.guardAndProceed). */}
          {renderBlokkadeBanner()}
        </div>
      );
    }
    // Geen suggestie (of afgewezen) → hetzelfde volledige zoekscherm als bij een plaatsing.
    return (
      <div className="fawkes-inboeken__step">
        <RelationPicker
          variant="step"
          label={t("uitboeken.field.owner")}
          stepTitle={t("inboeken.relation.findTitle")}
          stepInfo={t("uitboeken.relation.findInfo.return")}
          valueId={newOwnerId}
          valueLabel={newOwnerLabel}
          canCreate={canCreateRelatie}
          feedPath="release/reference/klanten"
          onBack={backStep}
          onPhaseChange={setRelationPhase}
          registerSave={registerRelationSave}
          onCreated={() => setNaAanmakenDoor(true)}
          onChange={(id, label) => {
            setNewOwnerId(id);
            setNewOwnerLabel(label);
            setReturnOwnerSource(id != null ? ORIGINAL_OWNER_SOURCE_MANUAL : null);
            setOverrideReden("");
          }}
          t={t}
        />
        {renderBlokkadeBanner()}
      </div>
    );
  }

  // Details step — stay card (day-count / intake / return / ambulance) + the editable cost lines seeded
  // with opvangkosten (days × €10) + ambulance/vet/admin. Mirrors the Jmix ReturnToOwnerDetailsFragment.
  function renderReturnDetails() {
    const dagen = returnDays === 1 ? t("uitboeken.returnCosts.day") : t("uitboeken.returnCosts.days");
    const ambulance =
      [returnStay?.ambulanceName, returnStay?.ambulanceRitNummer ? `(${returnStay.ambulanceRitNummer})` : null]
        .filter(Boolean)
        .join(" ") || "—";
    return (
      <div className="fawkes-inboeken__step">
        <div className="fawkes-inboeken__stay-card">
          <span className="fawkes-inboeken__step-title">{`${returnDays} ${dagen}`}</span>
          <span className="fawkes-inboeken__helper">
            {`${t("uitboeken.return.stay.intakeDate")}: ${returnStay?.intakeDate ?? "—"}`}
          </span>
          <span className="fawkes-inboeken__helper">
            {`${t("uitboeken.return.stay.releaseDate")}: ${releaseDate || "—"}`}
          </span>
          <span className="fawkes-inboeken__helper">
            {`${t("uitboeken.return.stay.ambulance")}: ${ambulance}`}
          </span>
        </div>
        <StepIntro title={t("uitboeken.returnCosts.header")} info={t("uitboeken.returnCosts.info")} />
        <div className="fawkes-inboeken__tarief-propose">
          <button
            type="button"
            className="fawkes-inboeken__link"
            onClick={proposeReturnTariff}
            disabled={tariefBusy}
          >
            ✨ {t("inboeken.costs.propose")}
          </button>
          {tariefNotice && (
            <span
              className={tariefNotice.type === "ok" ? "fawkes-inboeken__helper" : "fawkes-inboeken__muted"}
              role="status"
            >
              {tariefNotice.text}
            </span>
          )}
        </div>
        <ul className="fawkes-inboeken__cost-lines">
          {returnCosts.map((c) => (
            <li key={c.id} className="fawkes-inboeken__cost-line">
              {c.fixed ? (
                <span className="fui-field__label fawkes-inboeken__cost-product">{c.description}</span>
              ) : (
                <input
                  className="fui-field__input"
                  value={c.description}
                  placeholder={t("inboeken.costs.product")}
                  onChange={(e) =>
                    setReturnCosts((cur) => cur.map((x) => (x.id === c.id ? { ...x, description: e.target.value } : x)))
                  }
                />
              )}
              <input
                className="fui-field__input fawkes-inboeken__cost-amount"
                type="text"
                inputMode="decimal"
                value={c.amount}
                placeholder={t("inboeken.costs.amount.placeholder")}
                onChange={(e) =>
                  setReturnCosts((cur) => cur.map((x) => (x.id === c.id ? { ...x, amount: e.target.value } : x)))
                }
              />
              {/* Korting per regel. Dat is waar hij hoort: de btw van de korting is dan vanzelf die
                  van de regel waar hij op zit, en je kunt op het ene product wel korting geven en op
                  het andere niet (Maarten, 2026-10-24). */}
              <input
                className="fui-field__input fawkes-inboeken__cost-korting"
                type="text"
                inputMode="decimal"
                value={c.korting ?? ""}
                placeholder={t("korting.placeholder")}
                aria-label={t("korting.label")}
                title={t("korting.label")}
                onChange={(e) =>
                  setReturnCosts((cur) => cur.map((x) => (x.id === c.id ? { ...x, korting: e.target.value } : x)))
                }
              />
              <KostenregelBtw
                value={c.btw}
                onChange={(btw) => setReturnCosts((cur) => cur.map((x) => (x.id === c.id ? { ...x, btw } : x)))}
                percentages={btwPercentages}
                missing={parseAmount(c.amount) != null && c.btw == null}
                t={t}
              />
              {!c.fixed && (
                <button
                  type="button"
                  className="fawkes-inboeken__link"
                  onClick={() => setReturnCosts((cur) => cur.filter((x) => x.id !== c.id))}
                >
                  {t("inboeken.animals.remove")}
                </button>
              )}
            </li>
          ))}
        </ul>
        {returnBtwOntbreekt && (
          <div className="fawkes-inboeken__field-error" role="alert">{t("btw.verplicht")}</div>
        )}
        <button
          type="button"
          className="fawkes-inboeken__link"
          onClick={() => {
            setReturnCosts((cur) => [...cur, { id: returnCostSeq, description: "", amount: "" }]);
            setReturnCostSeq((n) => n + 1);
          }}
        >
          {t("inboeken.costs.add")}
        </button>
        <div className="fawkes-inboeken__cost-total fawkes-inboeken__cost-total--grand">
          <span className="fui-field__label">{t("inboeken.costs.total")}</span>
          <strong>{formatEuro(returnCostTotal, locale)}</strong>
        </div>
        <Field label={t("inboeken.field.opmerking")} value={returnDetails.remark}
          onChange={(e) => setReturnDetails({ remark: e.target.value })} />
      </div>
    );
  }

  // Payment step — reuses the adoption payment layout; return-to-owner records the desk payment for the
  // retourverklaring (no concept-factuur). Mirrors Jmix reusing AdoptionPaymentFragment for retour.
  function renderReturnPayment() {
    return renderPaymentMethods(
      returnPayment,
      setReturnPayment,
      returnPayMethod,
      setReturnPayMethod,
      returnCostTotal,
      "uitboeken.returnPayment.header",
      "uitboeken.returnPayment.info",
    );
  }

  function doPrintReturnVerklaring() {
    // Real uitboek-afdruk.pdf (§4 row 15: the Jmix retour Print button), mirroring the archived
    // retourverklaring; legacy client-side print only as fallback when the server render fails.
    downloadWizardDocumentPdf("RETOUR_EIGENAAR", buildValues()).catch(() => legacyPrintReturnVerklaring());
  }

  function legacyPrintReturnVerklaring() {
    printVerklaring({
      title: t("uitboeken.returnDeclaration.declTitle"),
      introText: t("uitboeken.returnDeclaration.declIntro"),
      declarationText: t("uitboeken.returnDeclaration.declText"),
      contactTitle: t("uitboeken.field.owner"),
      contactName: newOwnerLabel ?? "",
      contactAddress: "",
      contactPhone: "",
      contactEmail: "",
      animalsLabel: t("uitboeken.step.animals"),
      animals: selected.map((a) => a.label).join(", "),
      signatureLabel: t("inboeken.terms.signature"),
      signatureDataUrl: returnDeclaration.signature || null,
      dateLabel: t("inboeken.terms.date"),
      date: returnDeclaration.date,
      placeLabel: t("inboeken.terms.place"),
      place: returnDeclaration.place,
      costs: { label: t("inboeken.costs.total"), total: formatEuro(returnCostTotal, locale) },
    });
  }

  /**
   * Dezelfde opbouw als de afstandsverklaring in de inboekwizard: eerst de twee feitenkaarten
   * (eigenaar en dier), dan de verklaring met genummerde bepalingen en de kostenregels erin, dan
   * akkoord + handtekening met datum, plaats en het bedrag ernaast. Het stond hier als een losse
   * stapel invulvelden onder elkaar.
   */
  function renderReturnDeclaration() {
    const animal = selected[0];
    // De declText draagt zijn eigen "1. … 2. …"; een <ol> nummert opnieuw, dus dat prefix eraf.
    const clauses = t("uitboeken.returnDeclaration.declText")
      .split("\n")
      .map((line) => line.replace(/^\s*\d+\.\s*/, "").trim())
      .filter(Boolean);
    return (
      <div className="fawkes-inboeken__step">
        <StepIntro title={t("uitboeken.returnDeclaration.header")} info={t("uitboeken.returnDeclaration.info")} />
        <div className="fawkes-terms__cards">
          <section className="fawkes-terms__card">
            <span className="fawkes-terms__tag">{t("uitboeken.field.owner")}</span>
            <dl className="fawkes-terms__facts">
              <div><dt>{t("inboeken.terms.fact.naam")}</dt><dd>{ownerDetail?.naam ?? newOwnerLabel ?? "—"}</dd></div>
              <div><dt>{t("inboeken.terms.fact.adres")}</dt><dd>{addressLine(ownerDetail) || "—"}</dd></div>
              <div>
                <dt>{t("inboeken.terms.fact.telefoon")}</dt>
                <dd>{[ownerDetail?.telefoon, ownerDetail?.mobiel].filter(Boolean).join(" / ") || "—"}</dd>
              </div>
              <div><dt>{t("inboeken.terms.fact.email")}</dt><dd>{ownerDetail?.email ?? "—"}</dd></div>
            </dl>
          </section>
          <section className="fawkes-terms__card">
            <span className="fawkes-terms__tag">{t("inboeken.summary.section.dier")}</span>
            <dl className="fawkes-terms__facts">
              <div><dt>{t("inboeken.terms.fact.diernaam")}</dt><dd>{animal?.label || "—"}</dd></div>
              <div><dt>{t("inboeken.terms.fact.chipnummer")}</dt><dd>{animal?.chip || "—"}</dd></div>
              <div>
                <dt>{t("inboeken.terms.fact.soortRas")}</dt>
                <dd>{[animal?.soort, animal?.ras].filter(Boolean).join(" - ") || "—"}</dd>
              </div>
            </dl>
          </section>
        </div>
        <section className="fawkes-terms__declaration">
          <span className="fawkes-terms__tag">{t("uitboeken.returnDeclaration.declTitle")}</span>
          <p className="fawkes-terms__intro">{t("uitboeken.returnDeclaration.declIntro")}</p>
          <ol className="fawkes-terms__clauses">
            {clauses.map((clause, i) => (
              <li key={i}>
                {clause}
                {/* De kostenspecificatie staat in de bepaling waarin de eigenaar ze aanvaardt. */}
                {returnCostTotal > 0 && i === clauses.length - 1 && (
                  <table className="fawkes-terms__cost-table">
                    <tbody>
                      {returnCosts.map((c, j) => (
                        <tr key={j}>
                          <td>
                            {c.description}
                            {kortingVanRegel(c) > 0
                              && ` (${t("korting.label").toLowerCase()} - ${formatEuro(kortingVanRegel(c), locale)})`}
                          </td>
                          <td>{formatEuro(regelNaKorting(c), locale)}</td>
                        </tr>
                      ))}
                      <tr className="fawkes-terms__cost-total-row">
                        <td>{t("inboeken.costs.total")}</td>
                        <td>{formatEuro(returnCostTotal, locale)}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </li>
            ))}
          </ol>
        </section>
        <div className="fawkes-terms__agree-row">
          <label className="fawkes-terms__toggle">
            <input
              type="checkbox"
              role="switch"
              checked={returnDeclaration.accepted}
              onChange={(e) => setReturnDeclaration((st) => ({ ...st, accepted: e.target.checked }))}
            />
            <span>{t("uitboeken.returnDeclaration.agree")} *</span>
          </label>
        </div>
        {!returnDeclaration.accepted && (
          <span className="fawkes-inboeken__field-error">{t("uitboeken.returnDeclaration.agreeRequired")}</span>
        )}
        <div className="fawkes-terms__sign-row">
          <span className="fui-field__label fawkes-terms__sign-label">
            {t("inboeken.terms.signature")}
          </span>
          <div className="fawkes-terms__sign">
            <SignaturePad
              value={returnDeclaration.signature}
              onChange={(d) => setReturnDeclaration((st) => ({ ...st, signature: d }))}
              retryLabel={t("inboeken.terms.signatureRetry")}
              placeholder={t("inboeken.terms.signaturePlaceholder")}
            />
          </div>
          {/* Feiten naast de handtekening, niet als invulvelden — zoals bij de afstandsverklaring. */}
          <aside className="fawkes-terms__meta">
            <p className="fawkes-terms__meta-line">
              <strong>{t("inboeken.terms.date")}:</strong>{" "}
              {returnDeclaration.date ? formatDate(returnDeclaration.date, locale) : "—"}
            </p>
            <p className="fawkes-terms__meta-line">
              <strong>{t("inboeken.terms.place")}:</strong> {returnDeclaration.place || "—"}
            </p>
            {returnCostTotal > 0 && (
              <div className="fawkes-terms__meta-total">
                <span>{t("inboeken.costs.total")}</span>
                <strong>{formatEuro(returnCostTotal, locale)}</strong>
              </div>
            )}
          </aside>
        </div>
        <div className="fawkes-terms__print">
          <Button variant="secondary" onClick={doPrintReturnVerklaring}>
            <Printer size={16} aria-hidden="true" />
            {t("inboeken.terms.print")}
          </Button>
        </div>
      </div>
    );
  }
  function renderEuthanasia() {
    return (
      <div className="fawkes-inboeken__step fawkes-inboeken__grid">
        <Field
          label={t("uitboeken.euthanasia.date")}
          type="date"
          value={releaseDate}
          onChange={(e) => setReleaseDate(e.target.value)}
        />
        <SelectField
          label={t("uitboeken.euthanasia.performer")}
          value={euthanasia.performer}
          onChange={(e) => setEuthanasia((s) => ({ ...s, performer: e.target.value }))}
          options={[
            { value: "", label: t("uitboeken.euthanasia.performer.placeholder") },
            ...EUTHANASIA_PERFORMERS.map((p) => ({ value: p, label: p })),
          ]}
        />
        {euthanasiaExternal && (
          <>
            <Field label={t("uitboeken.euthanasia.vetName")} value={euthanasia.vetName}
              onChange={(e) => setEuthanasia((s) => ({ ...s, vetName: e.target.value }))} />
            <Field label={t("uitboeken.euthanasia.vetPractice")} value={euthanasia.vetPractice}
              onChange={(e) => setEuthanasia((s) => ({ ...s, vetPractice: e.target.value }))} />
          </>
        )}
        <SelectField
          label={t("uitboeken.euthanasia.reason")}
          value={euthanasia.reason}
          onChange={(e) => setEuthanasia((s) => ({ ...s, reason: e.target.value }))}
          options={[
            { value: "", label: t("uitboeken.euthanasia.reason.placeholder") },
            ...EUTHANASIA_REASONS.map((r) => ({ value: r, label: r })),
          ]}
        />
        <Field label={t("uitboeken.euthanasia.remarks")} value={euthanasia.remarks}
          onChange={(e) => setEuthanasia((s) => ({ ...s, remarks: e.target.value }))} />
        {euthanasiaBlocked && (
          <span className="fawkes-inboeken__field-error">{t("uitboeken.details.required")}</span>
        )}
      </div>
    );
  }

  function renderPetbaseChipStep() {
    return (
      <div className="fawkes-chipreg">
        <StepIntro
          title={deregisterMode ? t("uitboeken.chip.header") : t("uitboeken.petbaseChip.header")}
          info={deregisterMode ? t("uitboeken.chip.info") : t("uitboeken.petbaseChip.info")}
        />
        {chippedAnimals.map((a) => (
          <ReleaseChipCard
            key={a.id}
            animalId={a.id}
            label={a.label}
            draft={chipDraft[a.id]}
            onPatch={setChip}
            onSeedDetail={seedChipDetail}
            deregister={deregisterMode}
            t={t}
          />
        ))}
      </div>
    );
  }

  function renderDeceased() {
    return (
      <div className="fawkes-inboeken__step fawkes-inboeken__grid">
        <Field
          label={t("uitboeken.deceased.date")}
          type="date"
          value={releaseDate}
          onChange={(e) => setReleaseDate(e.target.value)}
        />
        <SelectField
          label={t("uitboeken.deceased.cause")}
          value={deceased.cause}
          onChange={(e) => setDeceased((s) => ({ ...s, cause: e.target.value }))}
          options={[
            { value: "", label: t("uitboeken.deceased.cause.placeholder") },
            ...DECEASED_CAUSES.map((c) => ({ value: c, label: c })),
          ]}
        />
        {deceased.cause === DECEASED_CAUSE_OTHER && (
          <Field label={t("uitboeken.deceased.causeOther")} value={deceased.causeOther}
            onChange={(e) => setDeceased((s) => ({ ...s, causeOther: e.target.value }))} />
        )}
        <SelectField
          label={t("uitboeken.deceased.externalVet")}
          value={deceased.externalVet ? "yes" : "no"}
          onChange={(e) => setDeceased((s) => ({ ...s, externalVet: e.target.value === "yes" }))}
          options={[
            { value: "no", label: t("common.no") },
            { value: "yes", label: t("common.yes") },
          ]}
        />
        {deceased.externalVet && (
          <>
            <Field label={t("uitboeken.deceased.vetName")} value={deceased.vetName}
              onChange={(e) => setDeceased((s) => ({ ...s, vetName: e.target.value }))} />
            <Field label={t("uitboeken.deceased.vetReference")} value={deceased.vetReference}
              onChange={(e) => setDeceased((s) => ({ ...s, vetReference: e.target.value }))} />
          </>
        )}
        <Field label={t("uitboeken.deceased.remarks")} value={deceased.remarks}
          onChange={(e) => setDeceased((s) => ({ ...s, remarks: e.target.value }))} />
        {deceasedBlocked && (
          <span className="fawkes-inboeken__field-error">{t("uitboeken.details.required")}</span>
        )}
      </div>
    );
  }

  /** The intake's found location as one readable line, for the choice card. */
  function foundLocationLine(loc: IntakeFoundLocation): string {
    const straat = [loc.straat, [loc.huisnummer, loc.huisnummerToevoeging].filter(Boolean).join("")]
      .filter(Boolean)
      .join(" ");
    const plaats = [loc.postcode, loc.woonplaats ?? loc.gemeenteLabel].filter(Boolean).join(" ");
    return [straat, plaats].filter(Boolean).join(", ") || (loc.gemeenteLabel ?? "—");
  }

  function renderNature() {
    // Chosen "op de vangplek": the address is the intake's, so it is read-only here.
    const vastOpVindplaats = naturePlace === "found" && intakeFoundLocation != null;
    return (
      <div className="fawkes-inboeken__step fawkes-inboeken__grid">
        <Field
          label={t("uitboeken.nature.date")}
          type="date"
          value={releaseDate}
          onChange={(e) => setReleaseDate(e.target.value)}
        />
        {/* Uitgezet op de vindplaats, of ergens anders. Only offered when the intake recorded a
            location — otherwise there is nothing to put back to and the form stands on its own. */}
        {/* The question leads the step, always — it is what gets counted afterwards ("hoeveel katten
            gingen terug naar hun vangplek?"), so it may not depend on whether the intake happened to
            record an address. Without one the first card carries no address line and cannot prefill
            anything; it still answers the question, and the operator types or clicks the spot. */}
        <div className="fawkes-inboeken__field--full">
          <span className="fui-field__label">{t("uitboeken.nature.place.label")}</span>
          <div className="fawkes-inboeken__choice fawkes-inboeken__choice--place">
            <button
              type="button"
              // Nothing recorded at intake → nothing to put the animal back to, so the card is dead and
              // "Andere locatie" carries the flow (the prefill effect selects it).
              disabled={intakeFoundLocation == null}
              className={naturePlace === "found" && intakeFoundLocation != null ? "is-selected" : ""}
              aria-pressed={naturePlace === "found" && intakeFoundLocation != null}
              onClick={() => {
                if (!intakeFoundLocation) return;
                setNaturePlace("found");
                fillFromFoundLocation(intakeFoundLocation);
              }}
            >
              {/* The pin marks the chosen card (the unselected one keeps its text centred). */}
              {naturePlace === "found" && intakeFoundLocation != null && (
                <span className="fawkes-inboeken__choice-icon" aria-hidden="true"><PinIcon width={14} height={14} /></span>
              )}
              <span className="fawkes-inboeken__choice-text">
                <span className="fawkes-inboeken__choice-title">{t("uitboeken.nature.place.found")}</span>
                <span className="fawkes-inboeken__muted">
                  {intakeFoundLocation
                    ? foundLocationLine(intakeFoundLocation)
                    : t("uitboeken.nature.place.foundUnknown")}
                </span>
              </span>
            </button>
            <button
              type="button"
              className={naturePlace === "other" ? "is-selected" : ""}
              aria-pressed={naturePlace === "other"}
              onClick={() => setNaturePlace("other")}
            >
              {naturePlace === "other" && (
                <span className="fawkes-inboeken__choice-icon" aria-hidden="true"><PinIcon width={14} height={14} /></span>
              )}
              <span className="fawkes-inboeken__choice-text">
                <span className="fawkes-inboeken__choice-title">{t("uitboeken.nature.place.other")}</span>
                <span className="fawkes-inboeken__muted">{t("uitboeken.nature.place.otherInfo")}</span>
              </span>
            </button>
          </div>
        </div>
        {/* Locked to the intake's spot: "op de vangplek" IS that address, so it is shown, not edited.
            Changing it means answering the question differently — that is what "Andere locatie" is. */}
        <ComboBox
          label={t("inboeken.field.gemeente")}
          value={nature.gemeenteId}
          disabled={vastOpVindplaats}
          onChange={(v) => setNature((s) => ({ ...s, gemeenteId: v }))}
          placeholder={t("inboeken.field.gemeente.placeholder")}
          options={gemeenten.map((g) => ({ value: String(g.id), label: g.label }))}
        />
        <Field label={t("inboeken.field.woonplaats")} value={nature.city} disabled={vastOpVindplaats}
          onChange={(e) => setNature((s) => ({ ...s, city: e.target.value }))} />
        <Field label={t("inboeken.field.postcode")} value={nature.postalCode} disabled={vastOpVindplaats}
          onChange={(e) => setNature((s) => ({ ...s, postalCode: e.target.value }))} />
        <Field label={t("inboeken.field.huisnummer")} value={nature.houseNumber} disabled={vastOpVindplaats}
          onChange={(e) => setNature((s) => ({ ...s, houseNumber: e.target.value }))} />
        <Field label={t("inboeken.field.toevoeging")} value={nature.houseNumberAddition} disabled={vastOpVindplaats}
          onChange={(e) => setNature((s) => ({ ...s, houseNumberAddition: e.target.value }))} />
        <Field label={t("inboeken.field.straat")} value={nature.street} disabled={vastOpVindplaats}
          onChange={(e) => setNature((s) => ({ ...s, street: e.target.value }))} />
        <Field label={t("uitboeken.nature.remark")} value={nature.remark}
          onChange={(e) => setNature((s) => ({ ...s, remark: e.target.value }))} />
        <label className="fawkes-inboeken__terms-agree">
          <input
            type="checkbox"
            checked={nature.vetCheck}
            onChange={(e) => setNature((s) => ({ ...s, vetCheck: e.target.checked }))}
          />
          <span>{t("uitboeken.nature.vetCheck")}</span>
        </label>
        <div className="fawkes-inboeken__map-block">
          <span className="fui-field__label">{t("inboeken.map.label")}</span>
          <span className="fawkes-inboeken__helper">
            {t(vastOpVindplaats ? "uitboeken.nature.mapLocked" : "uitboeken.nature.mapInfo")}
          </span>
          <FoundLocationMap
            lat={natureGeo.lat}
            lng={natureGeo.lng}
            // Locked to the intake's spot → look, don't move. Otherwise a click moves the pin, and
            // since that is no longer the found location the choice flips to "Andere locatie" rather
            // than leaving the card claiming something that isn't true.
            onPick={vastOpVindplaats ? undefined : (la, lo) => {
              setNaturePlace("other");
              handleNaturePick(la, lo);
            }}
            defaultCenter={natureMapCenter}
          />
        </div>
      </div>
    );
  }

  function renderExchangeParty() {
    return (
      <div className="fawkes-inboeken__step">
        <StepIntro title={t("uitboeken.exchange.partyHeader")} info={t("uitboeken.exchange.partyInfo")} />
        <ul className="fawkes-inboeken__results fawkes-exchange-party">
          {eligibleShelters.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                aria-pressed={newOwnerId === s.id}
                className={newOwnerId === s.id ? "is-selected" : ""}
                onClick={() => { setNewOwnerId(s.id); setNewOwnerLabel(s.naam); }}
              >
                <span className="fawkes-exchange-party__name">{s.naam}</span>
                <span className="fawkes-exchange-party__meta">
                  {[s.woonplaats, s.telefoon, s.email].filter(Boolean).join(" · ")}
                </span>
              </button>
            </li>
          ))}
          {eligibleShelters.length === 0 && (
            <li className="fawkes-inboeken__muted">{t("uitboeken.exchange.noShelters")}</li>
          )}
        </ul>
      </div>
    );
  }

  function renderExchangeDetails() {
    // Jmix ExchangeDetailsFragment: date + reason are both REQUIRED; the reason is a textarea
    // capped at 500 characters with its own placeholder.
    return (
      <div className="fawkes-inboeken__step fawkes-inboeken__grid">
        <Field
          label={t("uitboeken.field.releaseDate")}
          type="date"
          value={releaseDate}
          onChange={(e) => setReleaseDate(e.target.value)}
        />
        <TextareaField
          label={t("uitboeken.exchange.reason")}
          value={exchangeReason}
          maxLength={500}
          placeholder={t("uitboeken.exchange.reasonPlaceholder")}
          onChange={(e) => setExchangeReason(e.target.value)}
        />
        {exchangeBlocked && (
          <span className="fawkes-inboeken__field-error">{t("uitboeken.exchange.detailsRequired")}</span>
        )}
      </div>
    );
  }

  // ── per-flow rich summaries (audit §4 row 19 / §7 slice 9) ─────────────────────────────────────
  // Mirrors the Jmix summary fragments per vertreksoort: Adoption/ReturnToOwner/Euthanasia/Deceased/
  // NatureRelease/Exchange — dierfoto's + "Status: …", kostenregels-echo, akkoord/handtekening-
  // checkmarks (✓/•), chip-notitie, kaart-echo (uitgezet) en de vet-check-regel.

  /** The Jmix summary Dier card: labeled facts + "Status: …" left, photo box right — per animal. */
  function renderSummaryAnimals(statusKey: string) {
    return (
      <SummaryTile title={t("uitboeken.summary.section.dier")} full>
        {selected.map((a) => (
          <div key={a.id} className="fawkes-inboeken__summary-animal-row">
            <div className="fawkes-inboeken__summary-animal-facts">
              <SummaryRow label={t("uitboeken.summary.label.naam")} value={a.naam || "—"} />
              {a.chip && <SummaryRow label={t("uitboeken.summary.label.chip")} value={a.chip} />}
              <SummaryRow
                label={t("uitboeken.summary.label.diersoortRas")}
                value={[a.soort, a.ras].filter(Boolean).join(" - ") || "—"}
              />
              {a.geslacht && <SummaryRow label={t("uitboeken.summary.label.geslacht")} value={a.geslacht} />}
              <SummaryRow label={t("uitboeken.summary.label.status")} value={t(statusKey)} />
            </div>
            <SummaryPhotoBox fotoUrl={photoOf(a.id, a.fotoUrl)} name={a.naam} noPhotoLabel={t("inboeken.results.noPhoto")} />
          </div>
        ))}
        {selected.length === 0 && <SummaryRow label="" value="—" />}
      </SummaryTile>
    );
  }

  /** The betaling-echo tile: paid = amount + methods, unpaid = the red "Niet voldaan" line (Jmix). */
  function renderSummaryPayment(paidNow: number, payment: { pin: string; cash: string }) {
    const methods = [
      // Dezelfde namen als op de betaalstap zelf, anders heet dezelfde methode hier ineens anders.
      (parseAmount(payment.pin) ?? 0) > 0 ? t("inboeken.payment.pin") : null,
      (parseAmount(payment.cash) ?? 0) > 0 ? t("inboeken.payment.cash") : null,
    ]
      .filter(Boolean)
      .join(", ");
    return (
      <SummaryTile title={t("uitboeken.summary.section.betaling")}>
        {paidNow > 0 ? (
          <>
            <SummaryRow label={t("uitboeken.summary.label.paid")} value={formatEuro(paidNow, locale)} />
            <SummaryRow label={t("uitboeken.summary.label.paymentMethod")} value={methods || "—"} />
          </>
        ) : (
          <span className="fawkes-inboeken__line--not-paid">{t("uitboeken.summary.notPaid")}</span>
        )}
      </SummaryTile>
    );
  }

  function renderSummary() {
    const soortLabel = soorten.find((s) => String(s.id) === soortId)?.label ?? "—";
    const titleKey =
      option === ADOPTION ? "uitboeken.summary.title.adoption"
      : option === RETURN_TO_OWNER ? "uitboeken.summary.title.return"
      : option === NATURE_RELEASE ? "uitboeken.summary.title.nature"
      : option === EXCHANGE ? "uitboeken.summary.title.exchange"
      : "uitboeken.summary.title";
    const infoKey =
      option === ADOPTION ? "uitboeken.summary.info.adoption"
      : option === EUTHANASIA ? "uitboeken.summary.info.euthanasia"
      : option === DECEASED ? "uitboeken.summary.info.deceased"
      : "uitboeken.summary.info";
    return (
      <div className="fawkes-inboeken__step">
        <StepIntro title={t(titleKey)} info={t(infoKey)} />
        <div className="fawkes-inboeken__summary-grid">
          {option === ADOPTION && renderAdoptionSummary(soortLabel)}
          {option === RETURN_TO_OWNER && renderReturnSummary()}
          {option === EUTHANASIA && renderEuthanasiaSummary()}
          {option === DECEASED && renderDeceasedSummary()}
          {option === NATURE_RELEASE && renderNatureSummary()}
          {option === EXCHANGE && renderExchangeSummary()}
          {option === "DEFAULT" && (
            <SummaryTile title={t("uitboeken.summary.vertrek")}>
              <SummaryRow label={t("uitboeken.field.soort")} value={soortLabel} />
              <SummaryRow
                label={t("uitboeken.field.releaseDate")}
                value={releaseDate ? formatDate(releaseDate, locale) : "—"}
              />
            </SummaryTile>
          )}
        </div>
      </div>
    );
  }

  // Jmix AdoptionSummaryFragment: Dier + Plaatsing / Nieuwe eigenaar / Betaling + Kosten /
  // Plaatsingsovereenkomst (✓/✗) / Chipregistratie-notitie + de nazorg-kaart.
  function renderAdoptionSummary(soortLabel: string) {
    const hasChip = selected.some((a) => (a.chip ?? "").trim() !== "");
    return (
      <>
        {renderSummaryAnimals("uitboeken.summary.status.adoption")}
        <SummaryTile title={t("uitboeken.summary.section.plaatsing")}>
          <SummaryRow label={t("uitboeken.summary.label.releaseType")} value={soortLabel} />
          <SummaryRow
            label={t("uitboeken.summary.label.releaseDate")}
            value={releaseDate ? formatDate(releaseDate, locale) : "—"}
          />
        </SummaryTile>
        <SummaryTile title={t("uitboeken.summary.section.newOwner")}>
          <SummaryRow label={t("uitboeken.summary.label.naam")} value={ownerDetail?.naam ?? newOwnerLabel ?? "—"} />
          <SummaryRow label={t("uitboeken.summary.label.adres")} value={addressLine(ownerDetail) || "—"} />
          <SummaryRow label={t("uitboeken.summary.label.email")} value={ownerDetail?.email || "—"} />
          <SummaryRow
            label={t("uitboeken.summary.label.telefoon")}
            value={[ownerDetail?.telefoon, ownerDetail?.mobiel].filter(Boolean).join(" / ") || "—"}
          />
        </SummaryTile>
        <SummaryTile title={t("uitboeken.summary.section.kosten")}>
          {adoptionCosts.map((c) => (
            <div key={c.id} className="fawkes-inboeken__summary-cost-row">
              <span>
                {c.description || "—"}
                {/* De korting staat bij de regel waar hij op zit; anders lees je honderd euro kosten
                    met vijfenzeventig euro totaal eronder en klopt er iets niet dat wel klopt. */}
                {kortingVanRegel(c) > 0 && (
                  <small className="fawkes-inboeken__summary-korting">
                    {t("korting.label")} -&nbsp;{formatEuro(kortingVanRegel(c), locale)}
                  </small>
                )}
              </span>
              <span>{formatEuro(regelNaKorting(c), locale)}</span>
            </div>
          ))}
          {adoptionCosts.length === 0 && (
            <span className="fawkes-inboeken__muted">{t("uitboeken.summary.costs.empty")}</span>
          )}
          <div className="fawkes-inboeken__summary-cost-row fawkes-inboeken__summary-cost-row--total">
            <span>{t("uitboeken.summary.label.total")}</span>
            <span>{formatEuro(adoptionCostTotal, locale)}</span>
          </div>
        </SummaryTile>
        <SummaryTile title={t("uitboeken.summary.section.overeenkomst")}>
          <CheckRow ok={adoptionTerms.accepted} label={t("uitboeken.summary.terms.accepted")} />
          {(adoptionTerms.signature || adoptionTerms.signedLater) && (
            <CheckRow
              ok={!!adoptionTerms.signature}
              label={t(adoptionTerms.signature ? "uitboeken.summary.terms.signed" : "uitboeken.summary.terms.notSigned")}
            />
          )}
          <SummaryRow
            label={t("uitboeken.summary.label.signatureDate")}
            value={adoptionTerms.date ? formatDate(adoptionTerms.date, locale) : "—"}
          />
          <SummaryRow label={t("uitboeken.summary.label.signaturePlace")} value={adoptionTerms.place || "—"} />
        </SummaryTile>
        <SummaryTile title={t("uitboeken.summary.section.chip")}>
          <span className={hasChip ? "fawkes-inboeken__summary-chip-note" : "fawkes-inboeken__muted"}>
            {t(hasChip ? "uitboeken.summary.chip.note" : "uitboeken.summary.chip.none")}
          </span>
        </SummaryTile>
        {renderSummaryPayment(adoptionPaidNow, adoptionPayment)}
        {nazorgModuleEnabled && (
          /* Jmix AdoptionSummaryFragment nazorg card: default-on opt-in, only shown when the nazorg
             module is enabled; unticking skips the server's auto-issuance task. */
          <div className="fawkes-inboeken__summary-tile fawkes-inboeken__summary-tile--full fawkes-inboeken__nazorg-card">
            <label className="fawkes-inboeken__terms-agree">
              <input
                type="checkbox"
                checked={nazorgTask}
                onChange={(e) => setNazorgTask(e.target.checked)}
              />
              <span>{t("uitboeken.nazorg.check")}</span>
            </label>
            <span className="fawkes-inboeken__helper">{t("uitboeken.nazorg.hint")}</span>
          </div>
        )}
      </>
    );
  }

  // Jmix ReturnToOwnerSummaryFragment: Dier + Oorspronkelijke eigenaar / Retour gegevens /
  // Betaling ("Niet voldaan" in rood) / Chipregistratie + de print-knop (echte uitboek-afdruk.pdf).
  function renderReturnSummary() {
    const chip = selected.map((a) => (a.chip ?? "").trim()).find((c) => c !== "") ?? null;
    return (
      <>
        {renderSummaryAnimals("uitboeken.summary.status.return")}
        <SummaryTile title={t("uitboeken.summary.section.owner")}>
          <SummaryRow label={t("uitboeken.summary.label.owner")} value={ownerDetail?.naam ?? newOwnerLabel ?? "—"} />
          <SummaryRow label={t("uitboeken.summary.label.adres")} value={addressLine(ownerDetail) || "—"} />
          <SummaryRow label={t("uitboeken.summary.label.contact")} value={ownerDetail?.email || "—"} />
        </SummaryTile>
        <SummaryTile title={t("uitboeken.summary.section.retour")}>
          <SummaryRow
            label={t("uitboeken.summary.label.returnDate")}
            value={releaseDate ? formatDate(releaseDate, locale) : "—"}
          />
          <SummaryRow
            label={t("uitboeken.summary.label.notes")}
            value={returnDetails.remark || t("uitboeken.summary.notes.none")}
          />
        </SummaryTile>
        {renderSummaryPayment(returnPaidNow, returnPayment)}
        <SummaryTile title={t("uitboeken.summary.section.chip")}>
          <span className={chip ? "fawkes-inboeken__summary-chip-note" : "fawkes-inboeken__muted"}>
            {chip
              ? `${t("uitboeken.summary.chip.updatedFor")}: ${chip}`
              : t("uitboeken.summary.chip.noChipReturn")}
          </span>
        </SummaryTile>
        {/* Jmix ReturnToOwnerSummaryFragment print button (§1 row 17) — downloads the real
            uitboek-afdruk.pdf via the server renderer, the same document the archiver stores. */}
        <div className="fawkes-inboeken__summary-tile fawkes-inboeken__summary-print">
          <button type="button" className="fawkes-inboeken__link" onClick={doPrintReturnVerklaring}>
            {t("uitboeken.summary.printReturn")}
          </button>
        </div>
      </>
    );
  }

  // Jmix EuthanasiaFragment (summary): Dier + Euthanasie (datum / uitgevoerd door / praktijk /
  // reden) / Opmerkingen.
  function renderEuthanasiaSummary() {
    const performer = [euthanasia.performer, euthanasiaExternal ? euthanasia.vetName.trim() : null]
      .filter(Boolean)
      .join("; ");
    return (
      <>
        {renderSummaryAnimals("uitboeken.summary.status.euthanasia")}
        <SummaryTile title={t("uitboeken.summary.euthanasia")}>
          <SummaryRow
            label={t("uitboeken.summary.label.euthDate")}
            value={releaseDate ? formatDate(releaseDate, locale) : "—"}
          />
          <SummaryRow label={t("uitboeken.summary.label.performer")} value={performer || "—"} />
          {euthanasiaExternal && euthanasia.vetPractice.trim() !== "" && (
            <SummaryRow label={t("uitboeken.summary.label.practice")} value={euthanasia.vetPractice} />
          )}
          <SummaryRow label={t("uitboeken.summary.label.reason")} value={euthanasia.reason || "—"} />
        </SummaryTile>
        <SummaryTile title={t("uitboeken.summary.section.remarks")}>
          {euthanasia.remarks ? (
            <span>{euthanasia.remarks}</span>
          ) : (
            <span className="fawkes-inboeken__muted">{t("uitboeken.summary.remarks.none")}</span>
          )}
        </SummaryTile>
      </>
    );
  }

  // Jmix DeceasedSummaryFragment: Dier + Overlijden (datum / oorzaak / externe dierenarts) /
  // Opmerkingen.
  function renderDeceasedSummary() {
    const cause = (deceased.cause === DECEASED_CAUSE_OTHER ? deceased.causeOther : deceased.cause) || "—";
    const externalVet = deceased.externalVet
      ? [deceased.vetName.trim(), deceased.vetReference.trim() ? `(${deceased.vetReference.trim()})` : null]
          .filter(Boolean)
          .join(" ")
      : "";
    return (
      <>
        {renderSummaryAnimals("uitboeken.summary.status.deceased")}
        <SummaryTile title={t("uitboeken.summary.deceased")}>
          <SummaryRow
            label={t("uitboeken.summary.label.dateOfDeath")}
            value={releaseDate ? formatDate(releaseDate, locale) : "—"}
          />
          <SummaryRow label={t("uitboeken.summary.label.causeOfDeath")} value={cause} />
          <SummaryRow label={t("uitboeken.summary.label.externalVet")} value={externalVet || "—"} />
        </SummaryTile>
        <SummaryTile title={t("uitboeken.summary.section.remarks")}>
          {deceased.remarks ? (
            <span>{deceased.remarks}</span>
          ) : (
            <span className="fawkes-inboeken__muted">{t("uitboeken.summary.remarks.none")}</span>
          )}
        </SummaryTile>
      </>
    );
  }

  // Jmix NatureReleaseSummaryFragment: Dier + Uitzetgegevens (datum + vet-check-regel) /
  // Uitzetlocatie + de kaart-echo (read-only).
  function renderNatureSummary() {
    const gemeenteLabel = gemeenten.find((g) => String(g.id) === nature.gemeenteId)?.label ?? "—";
    const streetLine =
      [nature.street, [nature.houseNumber, nature.houseNumberAddition].filter(Boolean).join("")]
        .filter(Boolean)
        .join(" ");
    const postalCity = [nature.postalCode, nature.city].filter(Boolean).join(" ");
    return (
      <>
        {renderSummaryAnimals("uitboeken.summary.status.nature")}
        <SummaryTile title={t("uitboeken.summary.section.release")}>
          <SummaryRow
            label={t("uitboeken.summary.label.natureDate")}
            value={releaseDate ? formatDate(releaseDate, locale) : "—"}
          />
          <SummaryRow
            label={t("uitboeken.summary.label.vetCheck")}
            value={t(nature.vetCheck ? "uitboeken.summary.vetCheck.done" : "uitboeken.summary.vetCheck.notDone")}
          />
        </SummaryTile>
        <SummaryTile title={t("uitboeken.summary.nature")}>
          {intakeFoundLocation && (
            <SummaryRow
              label={t("uitboeken.nature.place.label")}
              value={t(naturePlace === "found" ? "uitboeken.nature.place.found" : "uitboeken.nature.place.other")}
            />
          )}
          <SummaryRow label={t("uitboeken.summary.label.straat")} value={streetLine || "—"} />
          <SummaryRow label={t("uitboeken.summary.label.postcodePlaats")} value={postalCity || "—"} />
          <SummaryRow label={t("inboeken.field.gemeente")} value={gemeenteLabel} />
          <SummaryRow label={t("inboeken.field.opmerking")} value={nature.remark || "—"} />
        </SummaryTile>
        <SummaryTile title={t("inboeken.map.label")} full>
          {/* Kaart-echo van de uitzetlocatie (Jmix stray-map-canvas--readonly). */}
          <div className="fawkes-inboeken__map-echo">
            <FoundLocationMap lat={natureGeo.lat} lng={natureGeo.lng} onPick={() => undefined} />
          </div>
        </SummaryTile>
      </>
    );
  }

  // Jmix ExchangeSummaryFragment: Dier + Ontvangende organisatie / Uitwisselingsgegevens.
  function renderExchangeSummary() {
    return (
      <>
        {renderSummaryAnimals("uitboeken.summary.status.exchange")}
        <SummaryTile title={t("uitboeken.summary.section.party")}>
          <SummaryRow
            label={t("uitboeken.summary.label.receivingParty")}
            value={ownerDetail?.naam ?? newOwnerLabel ?? "—"}
          />
          <SummaryRow label={t("uitboeken.summary.label.contact")} value={ownerDetail?.email || "—"} />
        </SummaryTile>
        <SummaryTile title={t("uitboeken.summary.section.exchange")}>
          <SummaryRow
            label={t("uitboeken.summary.label.exchangeDate")}
            value={releaseDate ? formatDate(releaseDate, locale) : "—"}
          />
          <SummaryRow
            label={t("uitboeken.summary.label.reason")}
            value={exchangeReason.trim() || t("uitboeken.summary.reason.none")}
          />
        </SummaryTile>
      </>
    );
  }
}

function SummaryTile({ title, full, children }: { title: string; full?: boolean; children: ReactNode }) {
  return (
    <div className={"fawkes-inboeken__summary-tile" + (full ? " fawkes-inboeken__summary-tile--full" : "")}>
      <span className="fawkes-inboeken__summary-tile-title">{title}</span>
      {children}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="fawkes-inboeken__summary-row">
      {label && <span className="fui-field__label">{label}</span>}
      <span>{value}</span>
    </div>
  );
}

/** The Jmix ✓/• check row (overeenkomst-echo): green check when done, muted bullet when not. */
function CheckRow({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="fawkes-inboeken__check-row">
      <span
        className={"fawkes-inboeken__check-icon" + (ok ? "" : " fawkes-inboeken__check-icon--off")}
        aria-hidden="true"
      >
        {ok ? "✓" : "•"}
      </span>
      <span>{label}</span>
    </div>
  );
}

/** The Jmix summary photo box: dierfoto (camera-fallback) with the animal's name underneath. */
function SummaryPhotoBox({ fotoUrl, name, noPhotoLabel }: { fotoUrl: string | null; name: string; noPhotoLabel: string }) {
  return (
    <div className="fawkes-inboeken__summary-photo-box">
      {fotoUrl ? (
        <img className="fawkes-inboeken__summary-photo" src={fotoUrl} alt={name} />
      ) : (
        <span
          className="fawkes-inboeken__summary-photo fawkes-inboeken__summary-photo--empty"
          title={noPhotoLabel}
          aria-label={noPhotoLabel}
        />
      )}
      <span className="fawkes-inboeken__summary-photo-name">{name || "—"}</span>
    </div>
  );
}

function animalLabel(a: AnimalSummary): string {
  const chip = a.chipPasnummer ? ` · ${a.chipPasnummer}` : "";
  const ras = a.ras ? ` (${a.ras})` : "";
  return `${a.naam}${ras}${chip}`;
}
